export type LandingLanguage = 'en' | 'it' | 'es' | 'de' | 'fr';

export const LANDING_STRINGS: Record<LandingLanguage, {
  nav: { login: string; getStarted: string };
  hero: {
    tagline: string;
    title: string;
    subtitle: string;
    cta: string;
    ctaSecondary: string;
  };
  heroRotating: {
    headline1: { title: string; subtitle: string };
    headline2: { title: string; subtitle: string };
    headline3: { title: string; subtitle: string };
    headline4: { title: string; subtitle: string };
    trustBadge: string;
    trustPills: { aiPowered: string; private: string; languages: string; patterns: string };
  };
  socialProof: {
    trusted: string;
    users: string;
    countries: string;
    moments: string;
  };
  pricing: {
    title: string;
    subtitle: string;
    monthly: string;
    annual: string;
    savePercent: string;
    free: {
      name: string;
      price: string;
      cta: string;
      features: string[];
    };
    premium: {
      name: string;
      priceMonthly: string;
      priceAnnual: string;
      priceAnnualFull: string;
      badge: string;
      cta: string;
      features: string[];
    };
    noCreditCard: string;
    cancelAnytime: string;
  };
  trust: {
    title: string;
    quote: string;
    bullets: string[];
  };
  faq: {
    title: string;
    questions: Array<{ q: string; a: string }>;
  };
  features: {
    title: string;
    subtitle: string;
    awareness: { title: string; desc: string };
    decisions: { title: string; desc: string };
    growth: { title: string; desc: string };
  };
  showcaseMirror: {
    label: string;
    title: string;
    desc: string;
    exampleText: string;
    exampleQuestion: string;
    tag1: string;
    tag2: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
  };
  showcaseValues: {
    label: string;
    title: string;
    desc: string;
    exampleValue1: string;
    exampleValue2: string;
    exampleValue3: string;
    exampleValue4: string;
    exampleValue5: string;
    exampleValue6: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
  };
  showcasePeople: {
    label: string;
    title: string;
    desc: string;
    relationship1: string;
    relationship2: string;
    mentioned: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
  };
  showcaseGoals: {
    label: string;
    title: string;
    desc: string;
    exampleGoal1: string;
    exampleGoal2: string;
    exampleGoal3: string;
    exampleGoal4: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
  };
  howItWorks: {
    title: string;
    step1: { title: string; desc: string };
    step2: { title: string; desc: string };
    step3: { title: string; desc: string };
  };
  personalities: {
    title: string;
    subtitle: string;
  };
  showcaseLabels: {
    seeInAction: string;
    psychologicalProfile: string;
    yourValues: string;
    addButton: string;
    importantPeople: string;
    family: string;
    love: string;
    work: string;
    growth: string;
    yourGoals: string;
    active: string;
    patternDetection: string;
    aiFindsPatterns: string;
    seeConnectionsMoments: string;
    getPersonalizedSuggestions: string;
    patternsIdentified: string;
    joyInConnection: string;
    emotional: string;
    observed: string;
    contextDescription1: string;
    momentExample1: string;
    workStress: string;
    situational: string;
    contextDescription2: string;
    swipeForMore: string;
    decisionIntelligence: string;
    resolveDoubtsWithData: string;
    compareOptions: string;
    comparisonQuestion: string;
    decisionAnalysis: string;
    pros: string;
    proAlignedSecurity: string;
    proMobilityFreedom: string;
    proHighEarning: string;
    cons: string;
    conTimeEffort: string;
    conShortTermStress: string;
    conEconomicUncertainty: string;
    seeFullAnalysis: string;
    proConAnalysis: string;
    alignmentPercentage: string;
    consciousDecisions: string;
    yourEvolution: string;
    trackHolisticGrowth: string;
    evolutionDescription: string;
    fiveDimensionsRadar: string;
    emotionFlowTrend: string;
    rhythmTracking: string;
    yourBestTime: string;
    bestTimeDay: string;
    heartSoulPeak: string;
    lifeIntelligence: string;
    intelligenceDescription: string;
    needsAttention: string;
    suggestionDescription: string;
    basedOnSources: string;
    deepProactiveInsights: string;
    insightsDescription: string;
    insightsBasedHistory: string;
    proactiveQuestions: string;
    identifyTensions: string;
    yourStory: string;
    findAnythingInstantly: string;
    storyDescription: string;
    semanticSearch: string;
    organizedCategories: string;
    momentConnectedGraph: string;
    moments: string;
    searchPlaceholder: string;
    allCategories: string;
    today: string;
    todayMoment: string;
    thisWeek: string;
    weekMoment: string;
    knowYouPercentage: string;
    contextAwareSuggestion: string;
    personalDashboard: string;
    journeyAtGlance: string;
    dashboardDescription: string;
    emotionFlowChart: string;
    dimensionsTrends: string;
    identifyBestTime: string;
    emotionFlow: string;
    lastDaysTracked: string;
    challengesToOvercome: string;
    challengeExample1: string;
    challengeDescription1: string;
    external: string;
    challengeExample2: string;
    challengeDescription2: string;
    swipeForChallenges: string;
    identifiedChallenges: string;
    faceRealObstacles: string;
    challengesDescription: string;
    challengeCategories: string;
    quantifiedIntensity: string;
    automaticPrioritization: string;
    yourAiGuides: string;
    aiSuggestValues: string;
    dimensionBody: string;
    dimensionMind: string;
    dimensionHeart: string;
    dimensionSoul: string;
    dimensionMatter: string;
    evolutionDescriptionFull: string;
    fiveDimensionsRadarFull: string;
    emotionFlowTrendFull: string;
    rhythmTrackingFull: string;
    goalAlignmentTitle: string;
    goalAlignmentDesc: string;
    goalAlignmentSubtitle: string;
    weeklyRhythmTitle: string;
    weeklyRhythmDesc: string;
    weeklyRhythmSubtitle: string;
    patternJoyDescription: string;
    patternJoySuggestion: string;
    patternWorkDescription: string;
    decisionOptionDescription: string;
    goalExample1: string;
    goalExample2: string;
    goalExample3: string;
    homeWelcomeDescription: string;
    aiProactiveDescription: string;
    searchContextDescription: string;
    intelligenceQuoteText: string;
    intelligenceQuoteWarn: string;
    intelligenceQuoteQuestion: string;
    actionTodayText: string;
    actionWeekText: string;
    joyQuoteText: string;
    challengeExample1Full: string;
    challengeDescription1Full: string;
    challengeExample2Full: string;
    challengeDescription2Full: string;
    challengesAiDescription: string;
    analyticsLabel: string;
    analyticsTitle: string;
    analyticsDescription: string;
    analyticsBullet1: string;
    analyticsBullet2: string;
    analyticsBullet3: string;
  };
  privacy: {
    title: string;
    subtitle: string;
    encryption: {
      title: string;
      desc: string;
      badge: string;
    };
    noSelling: {
      title: string;
      desc: string;
      badge: string;
    };
    control: {
      title: string;
      desc: string;
      badge: string;
    };
    compliance: string;
    humanStatement: string;
  };
  testimonial: {
    quote: string;
    author: string;
  };
  finalCta: {
    title: string;
    subtitle: string;
    button: string;
  };
  footer: {
    tagline: string;
    privacy: string;
    terms: string;
  };
  auth: {
    back: string;
    loginTitle: string;
    loginSubtitle: string;
    registerTitle: string;
    registerSubtitle: string;
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    loginButton: string;
    registerButton: string;
    orContinueWith: string;
    google: string;
    noAccount: string;
    haveAccount: string;
    signUp: string;
    signIn: string;
    passwordMismatch: string;
    passwordTooShort: string;
    connectionError: string;
    loginError: string;
    registerError: string;
  };
}> = {
  en: {
    nav: {
      login: "Sign In",
      getStarted: "Get Started Free"
    },
    hero: {
      tagline: "Your Conscious Life Operating System",
      title: "Finally understand yourself.",
      subtitle: "An AI companion that learns your story, recognizes your patterns, and helps you make decisions aligned with who you truly are.",
      cta: "Start Your Journey",
      ctaSecondary: "See How It Works"
    },
    heroRotating: {
      headline1: {
        title: "Your life story, all connected",
        subtitle: "See how your moments link together. Understand your patterns. Make better decisions."
      },
      headline2: {
        title: "12 AI guides that truly get you",
        subtitle: "From honest friend to stoic warrior. Choose the voice you need today."
      },
      headline3: {
        title: "Stop repeating the same mistakes",
        subtitle: "Your AI remembers your past choices and helps you learn from them."
      },
      headline4: {
        title: "Finally understand yourself. Deeply.",
        subtitle: "Capture moments. See what's repeating. Grow authentically."
      },
      trustBadge: "No credit card required • Start free today",
      trustPills: {
        aiPowered: "AI-Powered",
        private: "100% Private",
        languages: "5 Languages",
        patterns: "Pattern Recognition"
      }
    },
    socialProof: {
      trusted: "Trusted by conscious thinkers worldwide",
      users: "Active Users",
      countries: "Countries",
      moments: "Moments Tracked"
    },
    pricing: {
      title: "Start free. Upgrade when ready.",
      subtitle: "No credit card required. Cancel anytime.",
      monthly: "Monthly",
      annual: "Annual",
      savePercent: "Save 40%",
      free: {
        name: "Explorer",
        price: "Free Forever",
        cta: "Start Free",
        features: [
          "50 moments",
          "30 patterns detected",
          "20 decisions mapped",
          "100 chat messages",
          "All 12 AI personalities",
          "Voice & text input",
          "Basic analytics",
          "5 languages supported"
        ]
      },
      premium: {
        name: "Oracle",
        priceMonthly: "$9.99/month",
        priceAnnual: "$99/year",
        priceAnnualFull: "$119.88",
        badge: "Most Popular",
        cta: "Start 7-Day Free Trial",
        features: [
          "Unlimited moments",
          "Unlimited patterns",
          "Unlimited decisions",
          "Unlimited chat",
          "Advanced analytics",
          "Photo input",
          "Semantic search",
          "Profile evolution history",
          "Priority support",
          "Early access to new features"
        ]
      },
      noCreditCard: "No CC Required",
      cancelAnytime: "Cancel Anytime"
    },
    trust: {
      title: "Your thoughts stay yours",
      quote: "We're an independent company. We only answer to our users. Your data is fully encrypted at rest and in transit. We never sell your data or use it to train AI models.",
      bullets: [
        "Military-grade encryption (AES-256)",
        "Zero data selling. Ever.",
        "Export all your data anytime",
        "GDPR compliant",
        "Your data is NOT used to train AI"
      ]
    },
    faq: {
      title: "Frequently Asked Questions",
      questions: [
        {
          q: "What is Egovex?",
          a: "Egovex is your AI-powered companion that helps you understand yourself by connecting your life moments, spotting patterns, and helping you make better decisions."
        },
        {
          q: "How does it connect my moments?",
          a: "Every time you share a moment, our AI looks for similar experiences, patterns, and connections to your past. It's like having a wise friend who remembers everything and helps you see the bigger picture."
        },
        {
          q: "What are the 12 AI personalities?",
          a: "Each personality has distinct psychology, communication style, and coaching approach. Choose based on your mood and needs - from The Honest Friend to The Stoic Warrior."
        },
        {
          q: "Is my data private?",
          a: "Yes. Military-grade encryption. We never sell data or use it for AI training. Export anytime."
        },
        {
          q: "What's included in the free plan?",
          a: "50 moments, 30 patterns, 20 decisions, 100 chat messages, all 12 personalities, voice & text input, and basic analytics."
        },
        {
          q: "Can I use it in my language?",
          a: "Yes. Egovex supports 5 languages: English, Italian, Spanish, German, and French."
        },
        {
          q: "How is Egovex different from other journaling apps?",
          a: "Semantic graph, 12 AI personalities, decision intelligence, pattern detection, and profile evolution. It's not just journaling - it's a life OS."
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes. Cancel anytime from your account settings. No questions asked."
        }
      ]
    },
    features: {
      title: "Three ways Egovex helps you grow",
      subtitle: "Connect your life moments and see yourself clearly",
      awareness: {
        title: "See Your Patterns",
        desc: "Notice what keeps repeating in your life. Understand what triggers your emotions and behaviors."
      },
      decisions: {
        title: "Make Smarter Choices",
        desc: "Learn from your past decisions. See what worked and what didn't before choosing."
      },
      growth: {
        title: "Grow Your Way",
        desc: "Personalized insights from your real life, not generic advice. Progress that fits who you are."
      }
    },
    showcaseMirror: {
      label: "Your Mirror",
      title: "Who You Are Right Now",
      desc: "AI generates a deep psychological profile from all your moments. See your authentic self with clarity.",
      exampleText: "You are guided by a deep desire for freedom, meaningful connection with loved ones, and building a fulfilling family life. You move between the desire for stability and the wish to live in an environment that reflects authenticity and connection.",
      exampleQuestion: "How could you transform this balance into concrete reality?",
      tag1: "Growth-Oriented",
      tag2: "Family-Focused",
      bullet1: "Deep personality synthesis from your real data",
      bullet2: "Identifies core tensions between values and actions",
      bullet3: "Evolves as you grow and change"
    },
    showcaseValues: {
      label: "Your Values",
      title: "What Matters to You",
      desc: "Your values are the foundation of every decision. Tag and edit them as you discover what truly matters.",
      exampleValue1: "Freedom and exploration",
      exampleValue2: "Love and family connection",
      exampleValue3: "Personal authenticity",
      exampleValue4: "Financial security",
      exampleValue5: "Deep connection",
      exampleValue6: "Inner balance",
      bullet1: "Tag-based values you can always edit",
      bullet2: "AI suggests values based on your moments",
      bullet3: "Decision alignment shows values match"
    },
    showcasePeople: {
      label: "Important People",
      title: "Who Matters to You",
      desc: "Track the relationships that shape your life. See how often they appear in your story and what roles they play.",
      relationship1: "Partner",
      relationship2: "Friend and mentor",
      mentioned: "mentioned",
      bullet1: "Automatically detected from your moments",
      bullet2: "See relationship patterns over time",
      bullet3: "Understand who influences your decisions"
    },
    showcaseGoals: {
      label: "Your Goals",
      title: "Where You're Heading",
      desc: "Define your objectives in your own words. AI helps you track progress and stay aligned with what you truly want.",
      exampleGoal1: "Create a stable but free life, traveling with family",
      exampleGoal2: "Build a large, happy family",
      exampleGoal3: "Live a more authentic and connected life",
      exampleGoal4: "Achieve meaningful professional success",
      bullet1: "Goals in your own conversational style",
      bullet2: "AI tracks progress through your moments",
      bullet3: "See alignment between goals and daily actions"
    },
    howItWorks: {
      title: "How it works",
      step1: {
        title: "Capture your moments",
        desc: "Share your day, decisions, and feelings with your AI guide. Use voice, text, or photos."
      },
      step2: {
        title: "Your AI connects everything",
        desc: "See how moments link together. Patterns become clear. Insights emerge naturally."
      },
      step3: {
        title: "Make better decisions",
        desc: "When facing a choice, you have your entire life story as wisdom. Stop repeating mistakes."
      }
    },
    personalities: {
      title: "12 AI personalities, each one unique",
      subtitle: "Pick the voice you need today. From gentle support to tough love. Every guide understands you deeply."
    },
    showcaseLabels: {
      seeInAction: "See It In Action",
      psychologicalProfile: "Psychological Profile",
      yourValues: "Your Values",
      addButton: "Add",
      importantPeople: "Important People",
      family: "Family",
      love: "Love",
      work: "Work",
      growth: "Growth",
      yourGoals: "Your Goals",
      active: "active",
      patternDetection: "Pattern Detection",
      aiFindsPatterns: "AI finds repeating patterns in your life",
      seeConnectionsMoments: "See connections between moments and emotions",
      getPersonalizedSuggestions: "Get personalized suggestions to grow",
      patternsIdentified: "Patterns Identified",
      joyInConnection: "Joy in Couple Connection",
      emotional: "Emotional",
      observed: "observed",
      contextDescription1: "After intimate conversations with partner",
      momentExample1: "Moments of deep presence and vulnerability together",
      workStress: "Repetitive Work Stress",
      situational: "Situational",
      contextDescription2: "Work environment that doesn't align with values",
      swipeForMore: "← Swipe for more patterns",
      decisionIntelligence: "Decision Intelligence",
      resolveDoubtsWithData: "Resolve Doubts with Data",
      compareOptions: "Compare Options",
      comparisonQuestion: "Should I take the leap into freelancing or stay stable in my current job?",
      decisionAnalysis: "Decision Analysis",
      pros: "Pros",
      proAlignedSecurity: "Aligned with financial security desire",
      proMobilityFreedom: "Allows mobility and freedom",
      proHighEarning: "High earning potential",
      cons: "Cons",
      conTimeEffort: "Requires initial time and effort",
      conShortTermStress: "Short-term stress",
      conEconomicUncertainty: "Initial economic uncertainty",
      seeFullAnalysis: "See full analysis →",
      proConAnalysis: "PRO/CONS analysis based on your history",
      alignmentPercentage: "Alignment % with your values",
      consciousDecisions: "Conscious decisions, not impulses",
      yourEvolution: "Your Evolution",
      trackHolisticGrowth: "Track Your Holistic Growth",
      evolutionDescription: "Egovex tracks 5 vital dimensions of your wellbeing in a dynamic radar visualization. See where you're thriving and where you need care.",
      fiveDimensionsRadar: "5 vital dimensions with radar visualization",
      emotionFlowTrend: "Emotion flow and trends last 14 days",
      rhythmTracking: "Rhythm tracking: discover when you feel best",
      yourBestTime: "⭐ Your Best Time",
      bestTimeDay: "Friday Evening",
      heartSoulPeak: "Heart & Soul at peak levels",
      lifeIntelligence: "Life Intelligence",
      intelligenceDescription: "The AI proactively surfaces insights based on your life graph. It notices what you might miss and asks the right questions.",
      needsAttention: "Needs Attention",
      suggestionDescription: "Your Mind dimension is low (3.8/10). You mentioned work stress 4 times this week. Maybe it's time to explore why?",
      basedOnSources: "Based on 14 sources",
      deepProactiveInsights: "Deep and Proactive Insights",
      insightsDescription: "The AI doesn't wait for you to ask. It proactively notices tensions, patterns, and opportunities in your life graph.",
      insightsBasedHistory: "Insights based on your real history",
      proactiveQuestions: "Proactive questions that make you think",
      identifyTensions: "Identifies tensions between values and actions",
      yourStory: "Your Story",
      findAnythingInstantly: "Find Anything, Instantly",
      storyDescription: "Powerful semantic search lets you find any moment by meaning, not just keywords. Your entire journey, perfectly organized.",
      semanticSearch: "Semantic search: find by meaning, not just words",
      organizedCategories: "Organized by categories: Spirit, Finance, Connect",
      momentConnectedGraph: "Every moment connected to your life graph",
      moments: "MOMENTS",
      searchPlaceholder: "Search your moments...",
      allCategories: "All categories",
      today: "Today",
      todayMoment: "Amazing conversation with Sarah about moving abroad together. She's fully aligned.",
      thisWeek: "This Week",
      weekMoment: "Difficult client call. Felt undervalued. Starting to question if this job aligns with my values.",
      knowYouPercentage: "I know you at 82%",
      contextAwareSuggestion: "Based on your last 3 moments, it seems work stress is increasing. Want to talk about it?",
      personalDashboard: "Personal Dashboard",
      journeyAtGlance: "Your Journey at a Glance",
      dashboardDescription: "A living snapshot of your multidimensional life. Emotion flow, dimension trends, and your weekly 'best time' all in one view.",
      emotionFlowChart: "Emotion flow chart last 14 days",
      dimensionsTrends: "5 dimensions trends with sparklines",
      identifyBestTime: "Identify your weekly 'best time'",
      emotionFlow: "Emotion Flow",
      lastDaysTracked: "Last 14 days • 6 days tracked",
      challengesToOvercome: "Challenges to Overcome",
      challengeExample1: "Financial Instability Fear",
      challengeDescription1: "Recurring anxiety about money",
      external: "External",
      challengeExample2: "Parents' Pressure for Stability",
      challengeDescription2: "Family expectations conflict with desires",
      swipeForChallenges: "← Swipe for more challenges",
      identifiedChallenges: "Identified Challenges",
      faceRealObstacles: "Face Your Real Obstacles",
      challengesDescription: "The AI doesn't just show the good. It identifies real challenges blocking your growth and categorizes them for clarity.",
      challengeCategories: "Challenge categorization: internal, external, relational",
      quantifiedIntensity: "Quantified intensity (1-10)",
      automaticPrioritization: "Automatic challenge prioritization",
      yourAiGuides: "Your AI Guides",
      aiSuggestValues: "AI analyzes your moments to suggest values that reflect who you truly are",
      dimensionBody: "Body",
      dimensionMind: "Mind",
      dimensionHeart: "Heart",
      dimensionSoul: "Soul",
      dimensionMatter: "Matter",
      evolutionDescriptionFull: "See your evolution across 5 dimensions: Body, Mind, Heart, Soul, Matter. Radar chart, trends over time, and when you feel best.",
      fiveDimensionsRadarFull: "5 vital dimensions with radar visualization",
      emotionFlowTrendFull: "Emotion flow and trends last 14 days",
      rhythmTrackingFull: "Rhythm tracking: discover when you feel best",
      goalAlignmentTitle: "Goal Alignment",
      goalAlignmentDesc: "Track your progress towards what truly matters. See how your daily actions align with your long-term goals.",
      goalAlignmentSubtitle: "How well you're following your goals",
      weeklyRhythmTitle: "Your Rhythm",
      weeklyRhythmDesc: "Discover when you feel your best throughout the week. Identify patterns in your energy and mood.",
      weeklyRhythmSubtitle: "When you feel best",
      patternJoyDescription: "You show strong emotional joy in shared moments with your partner, especially through gestures of affection.",
      patternJoySuggestion: "You could value these moments more often to strengthen the connection.",
      patternWorkDescription: "Recurring frustration tied to bank routine, perceived as limiting.",
      decisionOptionDescription: "Develop a scalable AI project to ensure financial security",
      goalExample1: "Create a stable but free life, away from Milan, traveling with family",
      goalExample2: "Build a large, happy family",
      goalExample3: "Build a more authentic and connected life with Sarah",
      homeWelcomeDescription: "Your personalized home welcomes you with daily insights, meaningful quotes, and a complete picture of your journey.",
      aiProactiveDescription: "AI doesn't wait for you to ask. It sends insights when you need them, connecting dots between your moments.",
      searchContextDescription: "Search your moments like you'd search Google. AI understands context, not just keywords.",
      intelligenceQuoteText: "Hey, your values—freedom, connection with Sarah and family—are a roadmap. Remember your desire to live in London for authenticity and the struggle with the corporate grind? The tension is real.",
      intelligenceQuoteWarn: "Reveal what you tolerate and what you won't tolerate—like toxicity or confinement.",
      intelligenceQuoteQuestion: "What does choosing your values fully look like?",
      actionTodayText: "Sit down with Emily tonight—really sit, without distractions—and ask what weighs most about the current situation...",
      actionWeekText: "By end of week, map out three concrete steps that bring you closer to your goal of moving to Milan...",
      joyQuoteText: "Joy with Clara is not escape, it is the root of what you call freedom.",
      challengeExample1Full: "The monotony and dissatisfaction generated by the bank routine.",
      challengeDescription1Full: "You are deeply frustrated by your bank work, feeling it is limiting and unstimulating.",
      challengeExample2Full: "Sarah's job loss and sudden cut in bank commissions.",
      challengeDescription2Full: "Has caused economic instability and stress in the couple.",
      challengesAiDescription: "AI identifies your real life challenges: situational, relational, internal. Each with intensity and suggestions.",
      analyticsLabel: "Analytics Dashboard",
      analyticsTitle: "Read Your Inner Rhythm",
      analyticsDescription: "Every emotion has a pattern. Every high and low tells a story. See how your dimensions shift over time and discover the cycles that shape your life.",
      analyticsBullet1: "Spot rising or falling trends before you feel them",
      analyticsBullet2: "Understand which dimensions need attention now",
      analyticsBullet3: "See your progress as continuous waves, not isolated points"
    },
    privacy: {
      title: "Your privacy is sacred",
      subtitle: "Your deepest thoughts deserve the highest protection. We're an independent company that answers only to our users.",
      encryption: {
        title: "Military-Grade Encryption",
        desc: "Your data is protected with AES-256 encryption, the same standard used by banks and governments. End-to-end security at rest and in transit.",
        badge: "AES-256 Encryption"
      },
      noSelling: {
        title: "Never Sold",
        desc: "We don't sell your data. Ever. Period. We're not an advertising company. You're the customer, not the product.",
        badge: "0% Data Selling"
      },
      control: {
        title: "Full Control",
        desc: "Export or delete your data anytime with one click. It's your life, your data, your choice. No questions asked.",
        badge: "Export Anytime"
      },
      compliance: "Certifications & Compliance",
      humanStatement: "We're an independent company that answers only to our users. Your data is fully encrypted at rest and in transit. We never sell your data or use it to train AI models. Your privacy isn't a feature—it's our foundation."
    },
    testimonial: {
      quote: "It's like having a wise friend who remembers everything about me and helps me see myself clearly.",
      author: "A more conscious version of you"
    },
    finalCta: {
      title: "Ready to meet yourself?",
      subtitle: "Start free. No credit card required.",
      button: "Begin Now"
    },
    footer: {
      tagline: "Conscious living starts here.",
      privacy: "Privacy",
      terms: "Terms"
    },
    auth: {
      back: "Back",
      loginTitle: "Welcome back",
      loginSubtitle: "Sign in to continue",
      registerTitle: "Create your account",
      registerSubtitle: "Start your personal growth journey",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm password",
      firstName: "First name",
      lastName: "Last name",
      loginButton: "Sign In",
      registerButton: "Create Account",
      orContinueWith: "or continue with",
      google: "Continue with Google",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      signUp: "Sign up",
      signIn: "Sign in",
      passwordMismatch: "Passwords don't match",
      passwordTooShort: "Password must be at least 8 characters",
      connectionError: "Connection error. Please try again.",
      loginError: "Login error",
      registerError: "Registration error"
    }
  },
  it: {
    nav: {
      login: "Accedi",
      getStarted: "Inizia Gratis"
    },
    hero: {
      tagline: "Il Tuo Sistema Operativo per una Vita Consapevole",
      title: "Finalmente, capisciti.",
      subtitle: "Un compagno AI che impara la tua storia, riconosce i tuoi schemi e ti aiuta a prendere decisioni allineate con chi sei veramente.",
      cta: "Inizia il Tuo Viaggio",
      ctaSecondary: "Scopri Come Funziona"
    },
    heroRotating: {
      headline1: {
        title: "Tutta la tua storia, connessa",
        subtitle: "Vedi come i tuoi momenti si collegano. Capisci i tuoi schemi. Decidi meglio."
      },
      headline2: {
        title: "12 guide AI che ti capiscono davvero",
        subtitle: "Dall'amico sincero al guerriero stoico. Scegli la voce che ti serve oggi."
      },
      headline3: {
        title: "Smetti di ripetere gli stessi errori",
        subtitle: "La tua AI ricorda le tue scelte passate e ti aiuta a imparare da esse."
      },
      headline4: {
        title: "Finalmente, capisciti. Profondamente.",
        subtitle: "Cattura momenti. Vedi cosa si ripete. Cresci autenticamente."
      },
      trustBadge: "Nessuna carta richiesta • Inizia gratis oggi",
      trustPills: {
        aiPowered: "Potenziato da AI",
        private: "100% Privato",
        languages: "5 Lingue",
        patterns: "Rileva i Pattern"
      }
    },
    socialProof: {
      trusted: "Scelto da pensatori consapevoli in tutto il mondo",
      users: "Utenti Attivi",
      countries: "Paesi",
      moments: "Momenti Tracciati"
    },
    pricing: {
      title: "Inizia gratis. Passa a premium quando vuoi.",
      subtitle: "Nessuna carta di credito richiesta. Cancella quando vuoi.",
      monthly: "Mensile",
      annual: "Annuale",
      savePercent: "Risparmia 40%",
      free: {
        name: "Explorer",
        price: "Gratis Per Sempre",
        cta: "Inizia Gratis",
        features: [
          "50 momenti",
          "30 schemi rilevati",
          "20 decisioni mappate",
          "100 messaggi chat",
          "Tutte le 12 personalità AI",
          "Inserimento tramite voce e testo",
          "Analisi di base",
          "5 lingue supportate"
        ]
      },
      premium: {
        name: "Oracle",
        priceMonthly: "€9.99/mese",
        priceAnnual: "€99/anno",
        priceAnnualFull: "€119.88",
        badge: "Più Popolare",
        cta: "Prova Gratuita 7 Giorni",
        features: [
          "Momenti illimitati",
          "Schemi illimitati",
          "Decisioni illimitate",
          "Chat illimitata",
          "Analisi avanzate",
          "Inserimento tramite foto",
          "Ricerca semantica",
          "Storico evoluzione profilo",
          "Supporto prioritario",
          "Accesso anticipato nuove funzionalità"
        ]
      },
      noCreditCard: "Senza Carta",
      cancelAnytime: "Cancella Quando Vuoi"
    },
    trust: {
      title: "I tuoi pensieri restano tuoi",
      quote: "Siamo un'azienda indipendente. Rispondiamo solo ai nostri utenti. I tuoi dati sono completamente criptati a riposo e in transito. Non vendiamo mai i tuoi dati né li usiamo per addestrare l'AI.",
      bullets: [
        "Crittografia militare (AES-256)",
        "Zero vendita di dati. Mai.",
        "Esporta tutti i tuoi dati in qualsiasi momento",
        "Conforme GDPR",
        "I tuoi dati NON sono usati per addestrare l'AI"
      ]
    },
    faq: {
      title: "Domande Frequenti",
      questions: [
        {
          q: "Cos'è Egovex?",
          a: "Egovex è il tuo compagno AI che ti aiuta a capirti collegando i momenti della tua vita, trovando schemi ricorrenti e aiutandoti a prendere decisioni migliori."
        },
        {
          q: "Come collega i miei momenti?",
          a: "Ogni volta che condividi un momento, la nostra AI cerca esperienze simili, schemi e connessioni con il tuo passato. È come avere un amico saggio che ricorda tutto e ti aiuta a vedere il quadro generale."
        },
        {
          q: "Cosa sono le 12 personalità AI?",
          a: "Ogni personalità ha psicologia distinta, stile di comunicazione e approccio di coaching. Scegli in base al tuo umore e bisogni - dall'Amico Sincero al Guerriero Stoico."
        },
        {
          q: "I miei dati sono privati?",
          a: "Sì. Crittografia militare. Non vendiamo mai i dati né li usiamo per addestrare l'AI. Esporta quando vuoi."
        },
        {
          q: "Cosa è incluso nel piano gratuito?",
          a: "50 momenti, 30 schemi, 20 decisioni, 100 messaggi chat, tutte le 12 personalità, inserimento tramite voce e testo, e analisi di base."
        },
        {
          q: "Posso usarlo nella mia lingua?",
          a: "Sì. Egovex supporta 5 lingue: Inglese, Italiano, Spagnolo, Tedesco e Francese."
        },
        {
          q: "Come si differenzia Egovex dalle altre app di journaling?",
          a: "Grafo semantico, 12 personalità AI, intelligenza decisionale, rilevamento degli schemi ed evoluzione del profilo. Non è solo journaling - è un sistema operativo per la vita."
        },
        {
          q: "Posso cancellare in qualsiasi momento?",
          a: "Sì. Cancella in qualsiasi momento dalle impostazioni del tuo account. Nessuna domanda."
        }
      ]
    },
    features: {
      title: "Tre modi in cui Egovex ti aiuta a crescere",
      subtitle: "Collega i momenti della tua vita e vediti chiaramente",
      awareness: {
        title: "Vedi i Tuoi Schemi",
        desc: "Nota cosa continua a ripetersi nella tua vita. Capisci cosa scatena le tue emozioni e comportamenti."
      },
      decisions: {
        title: "Scegli Meglio",
        desc: "Impara dalle tue decisioni passate. Vedi cosa ha funzionato e cosa no prima di scegliere."
      },
      growth: {
        title: "Cresci a Modo Tuo",
        desc: "Intuizioni personalizzate dalla tua vita reale, non consigli generici. Progresso che si adatta a chi sei."
      }
    },
    showcaseMirror: {
      label: "Il Tuo Specchio",
      title: "Chi Sei Ora",
      desc: "L'AI genera un profilo psicologico profondo da tutti i tuoi momenti. Vedi il tuo io autentico con chiarezza.",
      exampleText: "Sei guidato da un profondo desiderio di libertà, connessione significativa con i tuoi cari e costruire una vita familiare appagante. Ti muovi tra il desiderio di stabilità e il desiderio di vivere in un ambiente che riflette autenticità e connessione.",
      exampleQuestion: "Come potresti trasformare questo equilibrio in realtà concreta?",
      tag1: "Orientato alla Crescita",
      tag2: "Focalizzato sulla Famiglia",
      bullet1: "Sintesi profonda della personalità dai tuoi dati reali",
      bullet2: "Identifica tensioni chiave tra valori e azioni",
      bullet3: "Evolve mentre cresci e cambi"
    },
    showcaseValues: {
      label: "I Tuoi Valori",
      title: "Cosa Conta Per Te",
      desc: "I tuoi valori sono la base di ogni decisione. Etichettali e modificali man mano che scopri cosa conta davvero.",
      exampleValue1: "Libertà ed esplorazione",
      exampleValue2: "Amore e connessione familiare",
      exampleValue3: "Autenticità personale",
      exampleValue4: "Sicurezza economica",
      exampleValue5: "Connessione profonda",
      exampleValue6: "Equilibrio interiore",
      bullet1: "Valori con tag che puoi sempre modificare",
      bullet2: "L'AI suggerisce valori basati sui tuoi momenti",
      bullet3: "L'allineamento decisionale mostra la corrispondenza dei valori"
    },
    showcasePeople: {
      label: "Persone Importanti",
      title: "Chi Conta Per Te",
      desc: "Traccia le relazioni che plasmano la tua vita. Vedi quanto spesso appaiono nella tua storia e che ruoli giocano.",
      relationship1: "Partner",
      relationship2: "Amico e mentore",
      mentioned: "menzionato",
      bullet1: "Rilevate automaticamente dai tuoi momenti",
      bullet2: "Vedi gli schemi relazionali nel tempo",
      bullet3: "Comprendi chi influenza le tue decisioni"
    },
    showcaseGoals: {
      label: "I Tuoi Obiettivi",
      title: "Dove Stai Andando",
      desc: "Definisci i tuoi obiettivi con le tue parole. L'AI ti aiuta a tracciare i progressi e rimanere allineato con ciò che vuoi davvero.",
      exampleGoal1: "Creare una vita stabile ma libera, viaggiando con la famiglia",
      exampleGoal2: "Costruire una famiglia numerosa e felice",
      exampleGoal3: "Vivere una vita più autentica e connessa",
      exampleGoal4: "Raggiungere un successo professionale significativo",
      bullet1: "Obiettivi nel tuo stile conversazionale",
      bullet2: "L'AI traccia i progressi attraverso i tuoi momenti",
      bullet3: "Vedi l'allineamento tra obiettivi e azioni quotidiane"
    },
    howItWorks: {
      title: "Come funziona",
      step1: {
        title: "Cattura i tuoi momenti",
        desc: "Condividi la tua giornata, decisioni e sentimenti con la tua guida AI. Usa voce, testo o foto."
      },
      step2: {
        title: "La tua AI connette tutto",
        desc: "Vedi come i momenti si collegano. Gli schemi diventano chiari. Le intuizioni emergono naturalmente."
      },
      step3: {
        title: "Decidi meglio",
        desc: "Quando affronti una scelta, hai tutta la tua storia come saggezza. Smetti di ripetere errori."
      }
    },
    personalities: {
      title: "12 personalità AI, ognuna unica",
      subtitle: "Scegli la voce di cui hai bisogno oggi. Dal supporto gentile alla verità senza filtri. Ogni guida ti capisce profondamente."
    },
    showcaseLabels: {
      seeInAction: "Vedi in Azione",
      psychologicalProfile: "Profilo Psicologico",
      yourValues: "I Tuoi Valori",
      addButton: "Aggiungi",
      importantPeople: "Persone Importanti",
      family: "Famiglia",
      love: "Amore",
      work: "Lavoro",
      growth: "Crescita",
      yourGoals: "I Tuoi Obiettivi",
      active: "attivi",
      patternDetection: "Rilevamento degli Schemi",
      aiFindsPatterns: "L'AI trova schemi ricorrenti nella tua vita",
      seeConnectionsMoments: "Vedi connessioni tra momenti ed emozioni",
      getPersonalizedSuggestions: "Ottieni suggerimenti personalizzati per crescere",
      patternsIdentified: "Schemi Identificati",
      joyInConnection: "Gioia nella Connessione di Coppia",
      emotional: "Emotivo",
      observed: "osservato",
      contextDescription1: "Dopo conversazioni intime con il partner",
      momentExample1: "Momenti di profonda presenza e vulnerabilità insieme",
      workStress: "Stress da Lavoro Ripetitivo",
      situational: "Situazionale",
      contextDescription2: "Ambiente lavorativo non allineato con i valori",
      swipeForMore: "← Scorri per altri schemi",
      decisionIntelligence: "Intelligenza Decisionale",
      resolveDoubtsWithData: "Risolvi i Dubbi con i Dati",
      compareOptions: "Comparazione Opzioni",
      comparisonQuestion: "Dovrei lanciarmi nel freelancing o rimanere stabile nel mio lavoro attuale?",
      decisionAnalysis: "Analisi Decisione",
      pros: "Pro",
      proAlignedSecurity: "Allineato con desiderio sicurezza finanziaria",
      proMobilityFreedom: "Permette mobilità e libertà",
      proHighEarning: "Alto potenziale guadagno",
      cons: "Contro",
      conTimeEffort: "Richiede tempo e sforzo iniziale",
      conShortTermStress: "Stress a breve termine",
      conEconomicUncertainty: "Incertezza economica iniziale",
      seeFullAnalysis: "Vedi analisi completa →",
      proConAnalysis: "Analisi PRO/CONTRO basata sulla tua storia",
      alignmentPercentage: "Percentuale allineamento con i tuoi valori",
      consciousDecisions: "Decisioni consapevoli, non impulsi",
      yourEvolution: "La Tua Evoluzione",
      trackHolisticGrowth: "Traccia la Tua Crescita Integrale",
      evolutionDescription: "Egovex traccia 5 dimensioni vitali del tuo benessere in una visualizzazione radar dinamica. Vedi dove stai prosperando e dove hai bisogno di cura.",
      fiveDimensionsRadar: "5 dimensioni vitali con visualizzazione radar",
      emotionFlowTrend: "Flusso emotivo e trend degli ultimi 14 giorni",
      rhythmTracking: "Monitoraggio del ritmo: scopri quando stai meglio",
      yourBestTime: "⭐ Il Tuo Momento Migliore",
      bestTimeDay: "Venerdì Sera",
      heartSoulPeak: "Cuore e Anima ai massimi livelli",
      lifeIntelligence: "Intelligenza di Vita",
      intelligenceDescription: "L'AI fa emergere proattivamente intuizioni basate sul tuo grafo di vita. Nota ciò che potresti perdere e pone le domande giuste.",
      needsAttention: "Necessita Attenzione",
      suggestionDescription: "La tua dimensione Mente è bassa (3.8/10). Hai menzionato stress lavorativo 4 volte questa settimana. Forse è il momento di esplorare perché?",
      basedOnSources: "Basato su 14 fonti",
      deepProactiveInsights: "Intuizioni Profonde e Proattive",
      insightsDescription: "L'AI non aspetta che tu chieda. Nota proattivamente tensioni, schemi e opportunità nel tuo grafo di vita.",
      insightsBasedHistory: "Intuizioni basate sulla tua storia reale",
      proactiveQuestions: "Domande proattive che ti fanno riflettere",
      identifyTensions: "Identifica tensioni tra valori e azioni",
      yourStory: "La Tua Storia",
      findAnythingInstantly: "Trova Tutto, Istantaneamente",
      storyDescription: "La potente ricerca semantica ti permette di trovare qualsiasi momento per significato, non solo parole chiave. L'intero tuo viaggio, perfettamente organizzato.",
      semanticSearch: "Ricerca semantica: trova per significato, non solo parole",
      organizedCategories: "Organizzato per categorie: Spirito, Finanze, Connessioni",
      momentConnectedGraph: "Ogni momento è collegato al tuo grafo di vita",
      moments: "MOMENTI",
      searchPlaceholder: "Cerca nei tuoi momenti...",
      allCategories: "Tutte categorie",
      today: "Oggi",
      todayMoment: "Conversazione straordinaria con Sarah sul trasferirci all'estero insieme. È completamente allineata.",
      thisWeek: "Questa Settimana",
      weekMoment: "Chiamata difficile con un cliente. Mi sono sentito sottovalutato. Inizio a chiedermi se questo lavoro si allinea con i miei valori.",
      knowYouPercentage: "Ti conosco all'82%",
      contextAwareSuggestion: "In base ai tuoi ultimi 3 momenti, sembra che lo stress lavorativo stia aumentando. Vuoi parlarne?",
      personalDashboard: "Dashboard Personale",
      journeyAtGlance: "Il Tuo Viaggio in un Colpo d'Occhio",
      dashboardDescription: "Un'istantanea viva della tua vita multidimensionale. Flusso emotivo, trend delle dimensioni e il tuo 'momento migliore' settimanale tutto in una vista.",
      emotionFlowChart: "Grafico del flusso emotivo degli ultimi 14 giorni",
      dimensionsTrends: "Trend delle 5 dimensioni con grafici a linea",
      identifyBestTime: "Identifica il tuo 'momento migliore' settimanale",
      emotionFlow: "Flusso Emotivo",
      lastDaysTracked: "Ultimi 14 giorni • 6 giorni tracciati",
      challengesToOvercome: "Sfide da Superare",
      challengeExample1: "Paura Instabilità Finanziaria",
      challengeDescription1: "Ansia ricorrente riguardo ai soldi",
      external: "Esterno",
      challengeExample2: "Pressione Genitori per Stabilità",
      challengeDescription2: "Aspettative familiari in conflitto con desideri",
      swipeForChallenges: "← Scorri per altre sfide",
      identifiedChallenges: "Sfide Identificate",
      faceRealObstacles: "Affronta gli Ostacoli Reali",
      challengesDescription: "L'AI non mostra solo il lato positivo. Identifica sfide reali che bloccano la tua crescita e le categorizza per chiarezza.",
      challengeCategories: "Categorizzazione sfide: interne, esterne, relazionali",
      quantifiedIntensity: "Intensità quantificata (1-10)",
      automaticPrioritization: "Prioritizzazione automatica delle sfide",
      yourAiGuides: "Le Tue Guide AI",
      aiSuggestValues: "L'AI analizza i tuoi momenti per suggerire valori che riflettono chi sei veramente",
      dimensionBody: "Corpo",
      dimensionMind: "Mente",
      dimensionHeart: "Cuore",
      dimensionSoul: "Anima",
      dimensionMatter: "Materia",
      evolutionDescriptionFull: "Vedi la tua evoluzione in 5 dimensioni: Corpo, Mente, Cuore, Anima, Materia. Grafico radar, trend nel tempo, e quando ti senti meglio.",
      fiveDimensionsRadarFull: "5 dimensioni vitali con visualizzazione radar",
      emotionFlowTrendFull: "Flusso emotivo e trend degli ultimi 14 giorni",
      rhythmTrackingFull: "Monitoraggio del ritmo: scopri quando stai meglio",
      goalAlignmentTitle: "Allineamento Obiettivi",
      goalAlignmentDesc: "Traccia i tuoi progressi verso ciò che conta davvero. Vedi come le tue azioni quotidiane si allineano con i tuoi obiettivi a lungo termine.",
      goalAlignmentSubtitle: "Quanto stai seguendo i tuoi obiettivi",
      weeklyRhythmTitle: "Il Tuo Ritmo",
      weeklyRhythmDesc: "Scopri quando ti senti al meglio durante la settimana. Identifica gli schemi ricorrenti nella tua energia e umore.",
      weeklyRhythmSubtitle: "Quando ti senti meglio",
      patternJoyDescription: "Mostri una forte gioia emotiva nei momenti condivisi con il tuo partner, specialmente attraverso gesti d'affetto.",
      patternJoySuggestion: "Potresti valorizzare questi momenti più spesso per rafforzare la connessione.",
      patternWorkDescription: "Frustrazione ricorrente legata alla routine bancaria, percepita come limitante.",
      decisionOptionDescription: "Sviluppare un progetto AI scalabile per garantire sicurezza finanziaria",
      goalExample1: "Creare una vita stabile ma libera, lontano da Milano, viaggiando con la famiglia",
      goalExample2: "Costruire una famiglia numerosa e felice",
      goalExample3: "Costruire una vita più autentica e connessa con Sarah",
      homeWelcomeDescription: "La tua home personalizzata ti accoglie con insight quotidiani, citazioni significative e un quadro completo del tuo percorso.",
      aiProactiveDescription: "L'AI non aspetta che tu chieda. Invia insight quando ne hai bisogno, collegando i punti tra i tuoi momenti.",
      searchContextDescription: "Cerca i tuoi momenti come cercheresti su Google. L'AI comprende il contesto, non solo le parole chiave.",
      intelligenceQuoteText: "Ehi, i tuoi valori—libertà, connessione con Sarah e la famiglia—sono una roadmap. Ricordi il tuo desiderio di vivere a Londra per autenticità e la lotta con il tran tran aziendale? La tensione è reale.",
      intelligenceQuoteWarn: "Rivelano ciò che tolleri e ciò che non tollererai—come la tossicità o la costrizione.",
      intelligenceQuoteQuestion: "Come sarebbe scegliere pienamente i tuoi valori?",
      actionTodayText: "Siediti con Emily stasera—siediti davvero, senza distrazioni—e chiedi cosa pesa di più sulla situazione attuale...",
      actionWeekText: "Entro fine settimana, delinea tre passi concreti che ti avvicinino al tuo obiettivo di trasferirti a Milano...",
      joyQuoteText: "La gioia con Clara non è fuga, è la radice di ciò che chiami libertà.",
      challengeExample1Full: "La monotonia e l'insoddisfazione generate dalla routine bancaria.",
      challengeDescription1Full: "Sei profondamente frustrato dal tuo lavoro in banca, sentendolo limitante e poco stimolante.",
      challengeExample2Full: "La perdita del lavoro di Sarah e il taglio improvviso delle commissioni bancarie.",
      challengeDescription2Full: "Ha causato instabilità economica e stress nella coppia.",
      challengesAiDescription: "L'AI identifica le tue sfide di vita reali: situazionali, relazionali, interne. Ognuna con intensità e suggerimenti.",
      analyticsLabel: "Dashboard Analitica",
      analyticsTitle: "Leggi il Tuo Ritmo Interiore",
      analyticsDescription: "Ogni emozione ha un pattern. Ogni alto e basso racconta una storia. Vedi come le tue dimensioni cambiano nel tempo e scopri i cicli che plasmano la tua vita.",
      analyticsBullet1: "Individua trend in crescita o calo prima di sentirli",
      analyticsBullet2: "Comprendi quali dimensioni hanno bisogno di attenzione ora",
      analyticsBullet3: "Vedi il tuo progresso come onde continue, non punti isolati"
    },
    privacy: {
      title: "La tua privacy è sacra",
      subtitle: "I tuoi pensieri più profondi meritano la massima protezione. Siamo un'azienda indipendente che risponde solo ai nostri utenti.",
      encryption: {
        title: "Crittografia Militare",
        desc: "I tuoi dati sono protetti con crittografia AES-256, lo stesso standard usato da banche e governi. Sicurezza end-to-end a riposo e in transito.",
        badge: "Crittografia AES-256"
      },
      noSelling: {
        title: "Mai Venduti",
        desc: "Non vendiamo i tuoi dati. Mai. Punto. Non siamo un'azienda pubblicitaria. Tu sei il cliente, non il prodotto.",
        badge: "0% Vendita Dati"
      },
      control: {
        title: "Controllo Totale",
        desc: "Esporta o elimina i tuoi dati in qualsiasi momento con un click. È la tua vita, i tuoi dati, la tua scelta. Senza domande.",
        badge: "Esporta Quando Vuoi"
      },
      compliance: "Certificazioni e Conformità",
      humanStatement: "Siamo un'azienda indipendente che risponde solo ai nostri utenti. I tuoi dati sono completamente criptati a riposo e in transito. Non vendiamo mai i tuoi dati né li usiamo per addestrare modelli AI. La tua privacy non è una funzionalità—è la nostra base."
    },
    testimonial: {
      quote: "È come avere un amico saggio che ricorda tutto di me e mi aiuta a vedermi chiaramente.",
      author: "Una versione più consapevole di te"
    },
    finalCta: {
      title: "Pronto a incontrare te stesso?",
      subtitle: "Inizia gratis. Nessuna carta di credito richiesta.",
      button: "Inizia Ora"
    },
    footer: {
      tagline: "La vita consapevole inizia qui.",
      privacy: "Privacy",
      terms: "Termini"
    },
    auth: {
      back: "Indietro",
      loginTitle: "Bentornato",
      loginSubtitle: "Accedi per continuare",
      registerTitle: "Crea il tuo account",
      registerSubtitle: "Inizia il tuo percorso di crescita personale",
      email: "Email",
      password: "Password",
      confirmPassword: "Conferma password",
      firstName: "Nome",
      lastName: "Cognome",
      loginButton: "Accedi",
      registerButton: "Crea Account",
      orContinueWith: "oppure continua con",
      google: "Continua con Google",
      noAccount: "Non hai un account?",
      haveAccount: "Hai già un account?",
      signUp: "Registrati",
      signIn: "Accedi",
      passwordMismatch: "Le password non corrispondono",
      passwordTooShort: "La password deve avere almeno 8 caratteri",
      connectionError: "Errore di connessione. Riprova.",
      loginError: "Errore durante il login",
      registerError: "Errore durante la registrazione"
    }
  },
  es: {
    nav: {
      login: "Iniciar Sesión",
      getStarted: "Empezar Gratis"
    },
    hero: {
      tagline: "Tu Sistema Operativo para una Vida Consciente",
      title: "Finalmente, conócete.",
      subtitle: "Un compañero de IA que aprende tu historia, reconoce tus patrones y te ayuda a tomar decisiones alineadas con quien realmente eres.",
      cta: "Comienza Tu Viaje",
      ctaSecondary: "Descubre Cómo Funciona"
    },
    heroRotating: {
      headline1: {
        title: "Toda tu historia, conectada",
        subtitle: "Mira cómo tus momentos se conectan. Comprende tus patrones. Toma mejores decisiones."
      },
      headline2: {
        title: "12 guías de IA que realmente te entienden",
        subtitle: "Del amigo honesto al guerrero estoico. Elige la voz que necesitas hoy."
      },
      headline3: {
        title: "Deja de repetir los mismos errores",
        subtitle: "Tu IA recuerda tus decisiones pasadas y te ayuda a aprender de ellas."
      },
      headline4: {
        title: "Finalmente, conócete. Profundamente.",
        subtitle: "Captura momentos. Observa lo que se repite. Crece auténticamente."
      },
      trustBadge: "Sin tarjeta de crédito • Comienza gratis hoy",
      trustPills: {
        aiPowered: "Potenciado por IA",
        private: "100% Privado",
        languages: "5 Idiomas",
        patterns: "Reconocimiento de Patrones"
      }
    },
    socialProof: {
      trusted: "Confiado por pensadores conscientes en todo el mundo",
      users: "Usuarios Activos",
      countries: "Países",
      moments: "Momentos Rastreados"
    },
    pricing: {
      title: "Comienza gratis. Actualiza cuando estés listo.",
      subtitle: "Sin tarjeta de crédito. Cancela en cualquier momento.",
      monthly: "Mensual",
      annual: "Anual",
      savePercent: "Ahorra 40%",
      free: {
        name: "Explorer",
        price: "Gratis Para Siempre",
        cta: "Comenzar Gratis",
        features: [
          "50 momentos",
          "30 patrones detectados",
          "20 decisiones mapeadas",
          "100 mensajes de chat",
          "Las 12 personalidades de IA",
          "Entrada de voz y texto",
          "Analíticas básicas",
          "5 idiomas soportados"
        ]
      },
      premium: {
        name: "Oracle",
        priceMonthly: "$9.99/mes",
        priceAnnual: "$99/año",
        priceAnnualFull: "$119.88",
        badge: "Más Popular",
        cta: "Comenzar Prueba Gratuita de 7 Días",
        features: [
          "Momentos ilimitados",
          "Patrones ilimitados",
          "Decisiones ilimitadas",
          "Chat ilimitado",
          "Analíticas avanzadas",
          "Entrada de fotos",
          "Búsqueda semántica",
          "Historial de evolución del perfil",
          "Soporte prioritario",
          "Acceso anticipado a nuevas funciones"
        ]
      },
      noCreditCard: "Sin Tarjeta",
      cancelAnytime: "Cancela Cuando Quieras"
    },
    trust: {
      title: "Tus pensamientos son solo tuyos",
      quote: "Somos una empresa independiente. Solo respondemos a nuestros usuarios. Tus datos están completamente encriptados en reposo y en tránsito. Nunca vendemos tus datos ni los usamos para entrenar modelos de IA.",
      bullets: [
        "Encriptación de grado militar (AES-256)",
        "Cero venta de datos. Nunca.",
        "Exporta todos tus datos en cualquier momento",
        "Cumplimiento GDPR",
        "Tus datos NO se usan para entrenar IA"
      ]
    },
    faq: {
      title: "Preguntas Frecuentes",
      questions: [
        {
          q: "¿Qué es Egovex?",
          a: "Egovex es tu compañero impulsado por IA que te ayuda a entenderte conectando los momentos de tu vida, encontrando patrones y ayudándote a tomar mejores decisiones."
        },
        {
          q: "¿Cómo conecta mis momentos?",
          a: "Cada vez que compartes un momento, nuestra IA busca experiencias similares, patrones y conexiones con tu pasado. Es como tener un amigo sabio que recuerda todo y te ayuda a ver el panorama general."
        },
        {
          q: "¿Qué son las 12 personalidades de IA?",
          a: "Cada personalidad tiene psicología distinta, estilo de comunicación y enfoque de coaching. Elige según tu estado de ánimo y necesidades, desde El Amigo Honesto hasta El Guerrero Estoico."
        },
        {
          q: "¿Mis datos son privados?",
          a: "Sí. Encriptación de grado militar. Nunca vendemos datos ni los usamos para entrenar IA. Exporta cuando quieras."
        },
        {
          q: "¿Qué incluye el plan gratuito?",
          a: "50 momentos, 30 patrones, 20 decisiones, 100 mensajes de chat, las 12 personalidades, entrada de voz y texto, y analíticas básicas."
        },
        {
          q: "¿Puedo usarlo en mi idioma?",
          a: "Sí. Egovex soporta 5 idiomas: Inglés, Italiano, Español, Alemán y Francés."
        },
        {
          q: "¿En qué se diferencia Egovex de otras apps de journaling?",
          a: "Grafo semántico, 12 personalidades de IA, inteligencia decisional, detección de patrones y evolución del perfil. No es solo journaling, es un sistema operativo de vida."
        },
        {
          q: "¿Puedo cancelar en cualquier momento?",
          a: "Sí. Cancela en cualquier momento desde la configuración de tu cuenta. Sin preguntas."
        }
      ]
    },
    features: {
      title: "Tres pilares del autoconocimiento",
      subtitle: "Egovex construye un grafo semántico de tu existencia",
      awareness: {
        title: "Autoconciencia Profunda",
        desc: "Descubre patrones que nunca habías notado. Entiende qué activa tus emociones y comportamientos."
      },
      decisions: {
        title: "Inteligencia Decisional",
        desc: "Aprende de tus elecciones pasadas. Ve decisiones similares y sus resultados antes de comprometerte."
      },
      growth: {
        title: "Crecimiento Auténtico",
        desc: "Perspectivas basadas en tus datos reales, no consejos genéricos. Progreso alineado con tus valores únicos."
      }
    },
    showcaseMirror: {
      label: "Tu Espejo",
      title: "Quién Eres Ahora",
      desc: "La IA genera un perfil psicológico profundo de todos tus momentos. Ve tu yo auténtico con claridad.",
      exampleText: "Estás guiado por un profundo deseo de libertad, conexión significativa con tus seres queridos y construir una vida familiar satisfactoria. Te mueves entre el deseo de estabilidad y el deseo de vivir en un entorno que refleje autenticidad y conexión.",
      exampleQuestion: "¿Cómo podrías transformar este equilibrio en realidad concreta?",
      tag1: "Orientado al Crecimiento",
      tag2: "Enfocado en la Familia",
      bullet1: "Síntesis profunda de personalidad de tus datos reales",
      bullet2: "Identifica tensiones clave entre valores y acciones",
      bullet3: "Evoluciona mientras creces y cambias"
    },
    showcaseValues: {
      label: "Tus Valores",
      title: "Lo Que Importa Para Ti",
      desc: "Tus valores son la base de cada decisión. Etiquétalos y modifícalos a medida que descubres lo que realmente importa.",
      exampleValue1: "Libertad y exploración",
      exampleValue2: "Amor y conexión familiar",
      exampleValue3: "Autenticidad personal",
      exampleValue4: "Seguridad financiera",
      exampleValue5: "Conexión profunda",
      exampleValue6: "Equilibrio interior",
      bullet1: "Valores con etiquetas que siempre puedes editar",
      bullet2: "La IA sugiere valores basados en tus momentos",
      bullet3: "La alineación decisional muestra coincidencia de valores"
    },
    showcasePeople: {
      label: "Personas Importantes",
      title: "Quién Importa Para Ti",
      desc: "Rastrea las relaciones que dan forma a tu vida. Ve con qué frecuencia aparecen en tu historia y qué roles juegan.",
      relationship1: "Pareja",
      relationship2: "Amigo y mentor",
      mentioned: "mencionado",
      bullet1: "Detectadas automáticamente de tus momentos",
      bullet2: "Ve patrones de relaciones a lo largo del tiempo",
      bullet3: "Comprende quién influye en tus decisiones"
    },
    showcaseGoals: {
      label: "Tus Objetivos",
      title: "Hacia Dónde Vas",
      desc: "Define tus objetivos con tus propias palabras. La IA te ayuda a seguir el progreso y mantenerte alineado con lo que realmente quieres.",
      exampleGoal1: "Crear una vida estable pero libre, viajando con la familia",
      exampleGoal2: "Construir una familia numerosa y feliz",
      exampleGoal3: "Vivir una vida más auténtica y conectada",
      exampleGoal4: "Lograr un éxito profesional significativo",
      bullet1: "Objetivos en tu estilo conversacional",
      bullet2: "La IA rastrea el progreso a través de tus momentos",
      bullet3: "Ve la alineación entre objetivos y acciones diarias"
    },
    howItWorks: {
      title: "Cómo Egovex transforma tu vida",
      step1: {
        title: "Comparte tus momentos",
        desc: "Habla con tu compañero de IA sobre tu día, decisiones y sentimientos. Voz o texto, tú eliges."
      },
      step2: {
        title: "La IA construye tu contexto",
        desc: "Cada momento se conecta con tu historia de vida. Los patrones emergen. Los insights surgen."
      },
      step3: {
        title: "Toma mejores decisiones",
        desc: "Cuando enfrentas una decisión, tienes toda tu historia como sabiduría. No más errores repetidos."
      }
    },
    personalities: {
      title: "12 personalidades de IA, cada una única",
      subtitle: "Elige la voz que necesitas hoy. Desde apoyo gentil hasta amor duro. Cada guía te entiende profundamente."
    },
    showcaseLabels: {
      seeInAction: "Míralo en Acción",
      psychologicalProfile: "Perfil Psicológico",
      yourValues: "Tus Valores",
      addButton: "Añadir",
      importantPeople: "Personas Importantes",
      family: "Familia",
      love: "Amor",
      work: "Trabajo",
      growth: "Crecimiento",
      yourGoals: "Tus Objetivos",
      active: "activos",
      patternDetection: "Detección de Patrones",
      aiFindsPatterns: "La IA encuentra patrones repetidos en tu vida",
      seeConnectionsMoments: "Ve conexiones entre momentos y emociones",
      getPersonalizedSuggestions: "Obtén sugerencias personalizadas para crecer",
      patternsIdentified: "Patrones Identificados",
      joyInConnection: "Alegría en la Conexión de Pareja",
      emotional: "Emocional",
      observed: "observado",
      contextDescription1: "Después de conversaciones íntimas con la pareja",
      momentExample1: "Momentos de presencia profunda y vulnerabilidad juntos",
      workStress: "Estrés Laboral Repetitivo",
      situational: "Situacional",
      contextDescription2: "Ambiente laboral que no se alinea con valores",
      swipeForMore: "← Desliza para más patrones",
      decisionIntelligence: "Inteligencia Decisional",
      resolveDoubtsWithData: "Resuelve Dudas con Datos",
      compareOptions: "Comparar Opciones",
      comparisonQuestion: "¿Debería lanzarme al freelancing o mantenerme estable en mi trabajo actual?",
      decisionAnalysis: "Análisis de Decisión",
      pros: "Pros",
      proAlignedSecurity: "Alineado con deseo de seguridad financiera",
      proMobilityFreedom: "Permite movilidad y libertad",
      proHighEarning: "Alto potencial de ganancias",
      cons: "Contras",
      conTimeEffort: "Requiere tiempo y esfuerzo inicial",
      conShortTermStress: "Estrés a corto plazo",
      conEconomicUncertainty: "Incertidumbre económica inicial",
      seeFullAnalysis: "Ver análisis completo →",
      proConAnalysis: "Análisis PRO/CONTRA basado en tu historia",
      alignmentPercentage: "Porcentaje de alineación con tus valores",
      consciousDecisions: "Decisiones conscientes, no impulsos",
      yourEvolution: "Tu Evolución",
      trackHolisticGrowth: "Rastrea Tu Crecimiento Holístico",
      evolutionDescription: "Egovex rastrea 5 dimensiones vitales de tu bienestar en una visualización de radar dinámica. Ve dónde estás prosperando y dónde necesitas cuidado.",
      fiveDimensionsRadar: "5 dimensiones vitales con visualización de radar",
      emotionFlowTrend: "Flujo emocional y tendencias últimos 14 días",
      rhythmTracking: "Seguimiento del ritmo: descubre cuándo te sientes mejor",
      yourBestTime: "⭐ Tu Mejor Momento",
      bestTimeDay: "Viernes por la Noche",
      heartSoulPeak: "Corazón y Alma en niveles máximos",
      lifeIntelligence: "Inteligencia de Vida",
      intelligenceDescription: "La IA genera proactivamente perspectivas basadas en tu grafo de vida. Nota lo que podrías perder y hace las preguntas correctas.",
      needsAttention: "Necesita Atención",
      suggestionDescription: "Tu dimensión Mente está baja (3.8/10). Mencionaste estrés laboral 4 veces esta semana. ¿Quizás es hora de explorar por qué?",
      basedOnSources: "Basado en 14 fuentes",
      deepProactiveInsights: "Perspectivas Profundas y Proactivas",
      insightsDescription: "La IA no espera a que preguntes. Nota proactivamente tensiones, patrones y oportunidades en tu grafo de vida.",
      insightsBasedHistory: "Perspectivas basadas en tu historia real",
      proactiveQuestions: "Preguntas proactivas que te hacen pensar",
      identifyTensions: "Identifica tensiones entre valores y acciones",
      yourStory: "Tu Historia",
      findAnythingInstantly: "Encuentra Cualquier Cosa, Al Instante",
      storyDescription: "La búsqueda semántica poderosa te permite encontrar cualquier momento por significado, no solo palabras clave. Todo tu viaje, perfectamente organizado.",
      semanticSearch: "Búsqueda semántica: encuentra por significado, no solo palabras",
      organizedCategories: "Organizado por categorías: Espíritu, Finanzas, Conexiones",
      momentConnectedGraph: "Cada momento conectado a tu grafo de vida",
      moments: "MOMENTOS",
      searchPlaceholder: "Busca en tus momentos...",
      allCategories: "Todas las categorías",
      today: "Hoy",
      todayMoment: "Conversación increíble con Sarah sobre mudarnos juntos al extranjero. Está completamente alineada.",
      thisWeek: "Esta Semana",
      weekMoment: "Llamada difícil con cliente. Me sentí infravalorado. Empiezo a cuestionarme si este trabajo se alinea con mis valores.",
      knowYouPercentage: "Te conozco al 82%",
      contextAwareSuggestion: "Según tus últimos 3 momentos, parece que el estrés laboral está aumentando. ¿Quieres hablar de ello?",
      personalDashboard: "Panel Personal",
      journeyAtGlance: "Tu Viaje de un Vistazo",
      dashboardDescription: "Una instantánea viva de tu vida multidimensional. Flujo emocional, tendencias de dimensiones y tu 'mejor momento' semanal todo en una vista.",
      emotionFlowChart: "Gráfico de flujo emocional últimos 14 días",
      dimensionsTrends: "Tendencias de 5 dimensiones con sparklines",
      identifyBestTime: "Identifica tu 'mejor momento' semanal",
      emotionFlow: "Flujo Emocional",
      lastDaysTracked: "Últimos 14 días • 6 días rastreados",
      challengesToOvercome: "Desafíos a Superar",
      challengeExample1: "Miedo a Inestabilidad Financiera",
      challengeDescription1: "Ansiedad recurrente sobre el dinero",
      external: "Externo",
      challengeExample2: "Presión de Padres por Estabilidad",
      challengeDescription2: "Expectativas familiares en conflicto con deseos",
      swipeForChallenges: "← Desliza para más desafíos",
      identifiedChallenges: "Desafíos Identificados",
      faceRealObstacles: "Enfrenta Tus Obstáculos Reales",
      challengesDescription: "La IA no solo muestra lo bueno. Identifica desafíos reales que bloquean tu crecimiento y los categoriza para mayor claridad.",
      challengeCategories: "Categorización de desafíos: internos, externos, relacionales",
      quantifiedIntensity: "Intensidad cuantificada (1-10)",
      automaticPrioritization: "Priorización automática de desafíos",
      yourAiGuides: "Tus Guías de IA",
      aiSuggestValues: "La IA analiza tus momentos para sugerir valores que reflejan quién eres realmente",
      dimensionBody: "Cuerpo",
      dimensionMind: "Mente",
      dimensionHeart: "Corazón",
      dimensionSoul: "Alma",
      dimensionMatter: "Materia",
      evolutionDescriptionFull: "Ve tu evolución en 5 dimensiones: Cuerpo, Mente, Corazón, Alma, Materia. Gráfico radar, tendencias en el tiempo y cuándo te sientes mejor.",
      fiveDimensionsRadarFull: "5 dimensiones vitales con visualización radar",
      emotionFlowTrendFull: "Flujo emocional y tendencias de los últimos 14 días",
      rhythmTrackingFull: "Seguimiento del ritmo: descubre cuándo te sientes mejor",
      goalAlignmentTitle: "Alineación de Objetivos",
      goalAlignmentDesc: "Sigue tu progreso hacia lo que realmente importa. Ve cómo tus acciones diarias se alinean con tus objetivos a largo plazo.",
      goalAlignmentSubtitle: "Qué tan bien estás siguiendo tus objetivos",
      weeklyRhythmTitle: "Tu Ritmo",
      weeklyRhythmDesc: "Descubre cuándo te sientes mejor durante la semana. Identifica patrones en tu energía y estado de ánimo.",
      weeklyRhythmSubtitle: "Cuándo te sientes mejor",
      patternJoyDescription: "Muestras una fuerte alegría emocional en momentos compartidos con tu pareja, especialmente a través de gestos de afecto.",
      patternJoySuggestion: "Podrías valorar estos momentos más a menudo para fortalecer la conexión.",
      patternWorkDescription: "Frustración recurrente ligada a la rutina bancaria, percibida como limitante.",
      decisionOptionDescription: "Desarrollar un proyecto de IA escalable para garantizar seguridad financiera",
      goalExample1: "Crear una vida estable pero libre, lejos de Milán, viajando con la familia",
      goalExample2: "Construir una familia numerosa y feliz",
      goalExample3: "Construir una vida más auténtica y conectada con Sarah",
      homeWelcomeDescription: "Tu hogar personalizado te recibe con perspectivas diarias, citas significativas y un panorama completo de tu viaje.",
      aiProactiveDescription: "La IA no espera a que preguntes. Envía perspectivas cuando las necesitas, conectando puntos entre tus momentos.",
      searchContextDescription: "Busca en tus momentos como buscarías en Google. La IA entiende el contexto, no solo palabras clave.",
      intelligenceQuoteText: "Oye, tus valores—libertad, conexión con Sarah y familia—son una hoja de ruta. ¿Recuerdas tu deseo de vivir en Londres por autenticidad y la lucha con la rutina corporativa? La tensión es real.",
      intelligenceQuoteWarn: "Revelan lo que toleras y lo que no tolerarás—como la toxicidad o el confinamiento.",
      intelligenceQuoteQuestion: "¿Cómo sería elegir plenamente tus valores?",
      actionTodayText: "Siéntate con Emily esta noche—realmente siéntate, sin distracciones—y pregunta qué pesa más sobre la situación actual...",
      actionWeekText: "Para fin de semana, traza tres pasos concretos que te acerquen a tu objetivo de mudarte a Milán...",
      joyQuoteText: "La alegría con Clara no es escape, es la raíz de lo que llamas libertad.",
      challengeExample1Full: "La monotonía y la insatisfacción generada por la rutina bancaria.",
      challengeDescription1Full: "Estás profundamente frustrado por tu trabajo bancario, sintiéndolo limitante y poco estimulante.",
      challengeExample2Full: "La pérdida del trabajo de Sarah y el recorte repentino en las comisiones bancarias.",
      challengeDescription2Full: "Ha causado inestabilidad económica y estrés en la pareja.",
      challengesAiDescription: "La IA identifica tus desafíos de vida reales: situacionales, relacionales, internos. Cada uno con intensidad y sugerencias.",
      analyticsLabel: "Panel Analítico",
      analyticsTitle: "Lee Tu Ritmo Interior",
      analyticsDescription: "Cada emoción tiene un patrón. Cada alto y bajo cuenta una historia. Ve cómo tus dimensiones cambian con el tiempo y descubre los ciclos que moldean tu vida.",
      analyticsBullet1: "Detecta tendencias al alza o a la baja antes de sentirlas",
      analyticsBullet2: "Comprende qué dimensiones necesitan atención ahora",
      analyticsBullet3: "Ve tu progreso como olas continuas, no puntos aislados"
    },
    privacy: {
      title: "Tu privacidad es sagrada",
      subtitle: "Tus pensamientos más profundos merecen la máxima protección. Somos una empresa independiente que solo responde a nuestros usuarios.",
      encryption: {
        title: "Encriptación de Grado Militar",
        desc: "Tus datos están protegidos con encriptación AES-256, el mismo estándar usado por bancos y gobiernos. Seguridad end-to-end en reposo y en tránsito.",
        badge: "Encriptación AES-256"
      },
      noSelling: {
        title: "Nunca Vendidos",
        desc: "No vendemos tus datos. Nunca. Punto. No somos una empresa de publicidad. Tú eres el cliente, no el producto.",
        badge: "0% Venta de Datos"
      },
      control: {
        title: "Control Total",
        desc: "Exporta o elimina tus datos en cualquier momento con un clic. Es tu vida, tus datos, tu elección. Sin preguntas.",
        badge: "Exporta Cuando Quieras"
      },
      compliance: "Certificaciones y Cumplimiento",
      humanStatement: "Somos una empresa independiente que solo responde a nuestros usuarios. Tus datos están completamente encriptados en reposo y en tránsito. Nunca vendemos tus datos ni los usamos para entrenar modelos de IA. Tu privacidad no es una característica—es nuestra base."
    },
    testimonial: {
      quote: "Es como tener un amigo sabio que recuerda todo sobre mí y me ayuda a verme claramente.",
      author: "Una versión más consciente de ti"
    },
    finalCta: {
      title: "¿Listo para conocerte?",
      subtitle: "Comienza gratis. Sin tarjeta de crédito.",
      button: "Comenzar Ahora"
    },
    footer: {
      tagline: "La vida consciente comienza aquí.",
      privacy: "Privacidad",
      terms: "Términos"
    },
    auth: {
      back: "Atrás",
      loginTitle: "Bienvenido de nuevo",
      loginSubtitle: "Inicia sesión para continuar",
      registerTitle: "Crea tu cuenta",
      registerSubtitle: "Comienza tu camino de crecimiento personal",
      email: "Email",
      password: "Contraseña",
      confirmPassword: "Confirmar contraseña",
      firstName: "Nombre",
      lastName: "Apellido",
      loginButton: "Iniciar Sesión",
      registerButton: "Crear Cuenta",
      orContinueWith: "o continúa con",
      google: "Continuar con Google",
      noAccount: "¿No tienes cuenta?",
      haveAccount: "¿Ya tienes cuenta?",
      signUp: "Regístrate",
      signIn: "Inicia sesión",
      passwordMismatch: "Las contraseñas no coinciden",
      passwordTooShort: "La contraseña debe tener al menos 8 caracteres",
      connectionError: "Error de conexión. Inténtalo de nuevo.",
      loginError: "Error al iniciar sesión",
      registerError: "Error en el registro"
    }
  },
  de: {
    nav: {
      login: "Anmelden",
      getStarted: "Kostenlos Starten"
    },
    hero: {
      tagline: "Dein Bewusstes Lebensbetriebssystem",
      title: "Verstehe dich endlich selbst.",
      subtitle: "Ein KI-Begleiter, der deine Geschichte lernt, deine Muster erkennt und dir hilft, Entscheidungen zu treffen, die zu dir passen.",
      cta: "Starte Deine Reise",
      ctaSecondary: "So Funktioniert Es"
    },
    heroRotating: {
      headline1: {
        title: "Deine ganze Geschichte, alles verbunden",
        subtitle: "Sieh, wie deine Momente sich verbinden. Verstehe deine Muster. Triff bessere Entscheidungen."
      },
      headline2: {
        title: "12 KI-Guides, die dich wirklich verstehen",
        subtitle: "Vom ehrlichen Freund zum stoischen Krieger. Wähle die Stimme, die du heute brauchst."
      },
      headline3: {
        title: "Hör auf, dieselben Fehler zu wiederholen",
        subtitle: "Deine KI erinnert sich an deine vergangenen Entscheidungen und hilft dir, daraus zu lernen."
      },
      headline4: {
        title: "Verstehe dich endlich. Tiefgehend.",
        subtitle: "Erfasse Momente. Sieh, was sich wiederholt. Wachse authentisch."
      },
      trustBadge: "Keine Kreditkarte erforderlich • Starte heute kostenlos",
      trustPills: {
        aiPowered: "KI-Gestützt",
        private: "100% Privat",
        languages: "5 Sprachen",
        patterns: "Mustererkennung"
      }
    },
    socialProof: {
      trusted: "Vertraut von bewussten Denkern weltweit",
      users: "Aktive Nutzer",
      countries: "Länder",
      moments: "Erfasste Momente"
    },
    pricing: {
      title: "Kostenlos starten. Upgrade wenn bereit.",
      subtitle: "Keine Kreditkarte erforderlich. Jederzeit kündbar.",
      monthly: "Monatlich",
      annual: "Jährlich",
      savePercent: "Spare 40%",
      free: {
        name: "Explorer",
        price: "Für Immer Kostenlos",
        cta: "Kostenlos Starten",
        features: [
          "50 Momente",
          "30 erkannte Muster",
          "20 kartierte Entscheidungen",
          "100 Chat-Nachrichten",
          "Alle 12 KI-Persönlichkeiten",
          "Sprach- und Texteingabe",
          "Basis-Analysen",
          "5 unterstützte Sprachen"
        ]
      },
      premium: {
        name: "Oracle",
        priceMonthly: "€9.99/Monat",
        priceAnnual: "€99/Jahr",
        priceAnnualFull: "€119.88",
        badge: "Am Beliebtesten",
        cta: "7-Tage Kostenlose Testversion Starten",
        features: [
          "Unbegrenzte Momente",
          "Unbegrenzte Muster",
          "Unbegrenzte Entscheidungen",
          "Unbegrenzter Chat",
          "Erweiterte Analysen",
          "Foto-Eingabe",
          "Semantische Suche",
          "Profil-Entwicklungshistorie",
          "Prioritäts-Support",
          "Frühzeitiger Zugang zu neuen Funktionen"
        ]
      },
      noCreditCard: "Keine Kreditkarte",
      cancelAnytime: "Jederzeit Kündbar"
    },
    trust: {
      title: "Deine Gedanken bleiben deine",
      quote: "Wir sind ein unabhängiges Unternehmen. Wir antworten nur unseren Nutzern. Deine Daten sind vollständig verschlüsselt im Ruhezustand und während der Übertragung. Wir verkaufen niemals deine Daten oder verwenden sie zum Trainieren von KI-Modellen.",
      bullets: [
        "Militärische Verschlüsselung (AES-256)",
        "Null Datenverkauf. Niemals.",
        "Exportiere alle deine Daten jederzeit",
        "DSGVO-konform",
        "Deine Daten werden NICHT zum Trainieren von KI verwendet"
      ]
    },
    faq: {
      title: "Häufig Gestellte Fragen",
      questions: [
        {
          q: "Was ist Egovex?",
          a: "Egovex ist dein KI-gestützter Begleiter, der dir hilft, dich selbst zu verstehen, indem er deine Lebensmomente verbindet, Muster findet und dir hilft, bessere Entscheidungen zu treffen."
        },
        {
          q: "Wie verbindet es meine Momente?",
          a: "Jedes Mal, wenn du einen Moment teilst, sucht unsere KI nach ähnlichen Erfahrungen, Mustern und Verbindungen zu deiner Vergangenheit. Es ist wie ein weiser Freund, der sich an alles erinnert und dir hilft, das große Ganze zu sehen."
        },
        {
          q: "Was sind die 12 KI-Persönlichkeiten?",
          a: "Jede Persönlichkeit hat eine eigene Psychologie, Kommunikationsstil und Coaching-Ansatz. Wähle basierend auf deiner Stimmung und Bedürfnissen - vom Ehrlichen Freund zum Stoischen Krieger."
        },
        {
          q: "Sind meine Daten privat?",
          a: "Ja. Militärische Verschlüsselung. Wir verkaufen niemals Daten oder verwenden sie für KI-Training. Exportiere jederzeit."
        },
        {
          q: "Was ist im kostenlosen Plan enthalten?",
          a: "50 Momente, 30 Muster, 20 Entscheidungen, 100 Chat-Nachrichten, alle 12 Persönlichkeiten, Sprach- und Texteingabe und Basis-Analysen."
        },
        {
          q: "Kann ich es in meiner Sprache nutzen?",
          a: "Ja. Egovex unterstützt 5 Sprachen: Englisch, Italienisch, Spanisch, Deutsch und Französisch."
        },
        {
          q: "Wie unterscheidet sich Egovex von anderen Journaling-Apps?",
          a: "Semantischer Graph, 12 KI-Persönlichkeiten, Entscheidungsintelligenz, Mustererkennung und Profil-Entwicklung. Es ist nicht nur Journaling - es ist ein Lebens-Betriebssystem."
        },
        {
          q: "Kann ich jederzeit kündigen?",
          a: "Ja. Kündige jederzeit in deinen Kontoeinstellungen. Keine Fragen."
        }
      ]
    },
    features: {
      title: "Drei Säulen der Selbsterkenntnis",
      subtitle: "Egovex erstellt einen semantischen Graphen deiner Existenz",
      awareness: {
        title: "Tiefes Selbstbewusstsein",
        desc: "Entdecke Muster, die dir nie aufgefallen sind. Verstehe, was deine Emotionen und Verhaltensweisen auslöst."
      },
      decisions: {
        title: "Entscheidungsintelligenz",
        desc: "Lerne aus deinen vergangenen Entscheidungen. Sieh ähnliche Entscheidungen und ihre Ergebnisse."
      },
      growth: {
        title: "Authentisches Wachstum",
        desc: "Erkenntnisse basierend auf deinen echten Daten, keine generischen Ratschläge. Fortschritt im Einklang mit deinen Werten."
      }
    },
    showcaseMirror: {
      label: "Dein Spiegel",
      title: "Wer Du Jetzt Bist",
      desc: "Die KI generiert ein tiefes psychologisches Profil aus all deinen Momenten. Sieh dein authentisches Selbst mit Klarheit.",
      exampleText: "Du wirst von einem tiefen Wunsch nach Freiheit, bedeutungsvoller Verbindung mit deinen Liebsten und dem Aufbau eines erfüllenden Familienlebens geleitet. Du bewegst dich zwischen dem Wunsch nach Stabilität und dem Wunsch, in einer Umgebung zu leben, die Authentizität und Verbindung widerspiegelt.",
      exampleQuestion: "Wie könntest du dieses Gleichgewicht in konkrete Realität verwandeln?",
      tag1: "Wachstumsorientiert",
      tag2: "Familienfokussiert",
      bullet1: "Tiefe Persönlichkeitssynthese aus deinen echten Daten",
      bullet2: "Identifiziert Kernspannungen zwischen Werten und Handlungen",
      bullet3: "Entwickelt sich, während du wächst und dich veränderst"
    },
    showcaseValues: {
      label: "Deine Werte",
      title: "Was Dir Wichtig Ist",
      desc: "Deine Werte sind die Grundlage jeder Entscheidung. Tagge und bearbeite sie, während du entdeckst, was wirklich zählt.",
      exampleValue1: "Freiheit und Erkundung",
      exampleValue2: "Liebe und familiäre Verbindung",
      exampleValue3: "Persönliche Authentizität",
      exampleValue4: "Finanzielle Sicherheit",
      exampleValue5: "Tiefe Verbindung",
      exampleValue6: "Inneres Gleichgewicht",
      bullet1: "Tag-basierte Werte, die du immer bearbeiten kannst",
      bullet2: "KI schlägt Werte basierend auf deinen Momenten vor",
      bullet3: "Entscheidungs-Alignment zeigt Werteübereinstimmung"
    },
    showcasePeople: {
      label: "Wichtige Menschen",
      title: "Wer Dir Wichtig Ist",
      desc: "Verfolge die Beziehungen, die dein Leben prägen. Sieh, wie oft sie in deiner Geschichte erscheinen und welche Rollen sie spielen.",
      relationship1: "Partner",
      relationship2: "Freund und Mentor",
      mentioned: "erwähnt",
      bullet1: "Automatisch aus deinen Momenten erkannt",
      bullet2: "Sieh Beziehungsmuster im Laufe der Zeit",
      bullet3: "Verstehe, wer deine Entscheidungen beeinflusst"
    },
    showcaseGoals: {
      label: "Deine Ziele",
      title: "Wohin Du Gehst",
      desc: "Definiere deine Ziele mit deinen eigenen Worten. Die KI hilft dir, Fortschritte zu verfolgen und mit dem verbunden zu bleiben, was du wirklich willst.",
      exampleGoal1: "Ein stabiles aber freies Leben schaffen, mit der Familie reisen",
      exampleGoal2: "Eine große, glückliche Familie aufbauen",
      exampleGoal3: "Ein authentischeres und verbundeneres Leben führen",
      exampleGoal4: "Bedeutenden beruflichen Erfolg erreichen",
      bullet1: "Ziele in deinem Gesprächsstil",
      bullet2: "KI verfolgt Fortschritte durch deine Momente",
      bullet3: "Sieh Alignment zwischen Zielen und täglichen Handlungen"
    },
    howItWorks: {
      title: "Wie Egovex dein Leben transformiert",
      step1: {
        title: "Teile deine Momente",
        desc: "Sprich mit deinem KI-Begleiter über deinen Tag, Entscheidungen und Gefühle. Sprache oder Text."
      },
      step2: {
        title: "KI baut deinen Kontext auf",
        desc: "Jeder Moment verbindet sich mit deiner Lebensgeschichte. Muster entstehen. Erkenntnisse tauchen auf."
      },
      step3: {
        title: "Triff bessere Entscheidungen",
        desc: "Bei jeder Entscheidung hast du deine gesamte Geschichte als Weisheit. Keine wiederholten Fehler mehr."
      }
    },
    personalities: {
      title: "12 KI-Persönlichkeiten, jede einzigartig",
      subtitle: "Wähle die Stimme, die du heute brauchst. Von sanfter Unterstützung bis zu harter Liebe. Jeder Guide versteht dich zutiefst."
    },
    showcaseLabels: {
      seeInAction: "Sieh es in Aktion",
      psychologicalProfile: "Psychologisches Profil",
      yourValues: "Deine Werte",
      addButton: "Hinzufügen",
      importantPeople: "Wichtige Menschen",
      family: "Familie",
      love: "Liebe",
      work: "Arbeit",
      growth: "Wachstum",
      yourGoals: "Deine Ziele",
      active: "aktiv",
      patternDetection: "Mustererkennung",
      aiFindsPatterns: "KI findet sich wiederholende Muster in deinem Leben",
      seeConnectionsMoments: "Sieh Verbindungen zwischen Momenten und Emotionen",
      getPersonalizedSuggestions: "Erhalte personalisierte Vorschläge zum Wachsen",
      patternsIdentified: "Identifizierte Muster",
      joyInConnection: "Freude an Partnerverbindung",
      emotional: "Emotional",
      observed: "beobachtet",
      contextDescription1: "Nach intimen Gesprächen mit Partner",
      momentExample1: "Momente tiefer Präsenz und Verletzlichkeit zusammen",
      workStress: "Wiederholter Arbeitsstress",
      situational: "Situativ",
      contextDescription2: "Arbeitsumgebung, die nicht mit Werten übereinstimmt",
      swipeForMore: "← Wische für mehr Muster",
      decisionIntelligence: "Entscheidungsintelligenz",
      resolveDoubtsWithData: "Löse Zweifel mit Daten",
      compareOptions: "Optionen Vergleichen",
      comparisonQuestion: "Soll ich den Sprung ins Freelancing wagen oder in meinem aktuellen Job stabil bleiben?",
      decisionAnalysis: "Entscheidungsanalyse",
      pros: "Vorteile",
      proAlignedSecurity: "Ausgerichtet auf Wunsch nach finanzieller Sicherheit",
      proMobilityFreedom: "Ermöglicht Mobilität und Freiheit",
      proHighEarning: "Hohes Verdienstpotenzial",
      cons: "Nachteile",
      conTimeEffort: "Erfordert anfängliche Zeit und Mühe",
      conShortTermStress: "Kurzfristiger Stress",
      conEconomicUncertainty: "Anfängliche wirtschaftliche Unsicherheit",
      seeFullAnalysis: "Vollständige Analyse ansehen →",
      proConAnalysis: "PRO/CONTRA-Analyse basierend auf deiner Geschichte",
      alignmentPercentage: "Übereinstimmung % mit deinen Werten",
      consciousDecisions: "Bewusste Entscheidungen, keine Impulse",
      yourEvolution: "Deine Evolution",
      trackHolisticGrowth: "Verfolge Dein Ganzheitliches Wachstum",
      evolutionDescription: "Egovex verfolgt 5 vitale Dimensionen deines Wohlbefindens in einer dynamischen Radar-Visualisierung. Sieh, wo du gedeihst und wo du Pflege brauchst.",
      fiveDimensionsRadar: "5 vitale Dimensionen mit Radar-Visualisierung",
      emotionFlowTrend: "Emotionsfluss und Trends der letzten 14 Tage",
      rhythmTracking: "Rhythmus-Tracking: Entdecke, wann du dich am besten fühlst",
      yourBestTime: "⭐ Deine Beste Zeit",
      bestTimeDay: "Freitagabend",
      heartSoulPeak: "Heart & Soul auf Höchstniveau",
      lifeIntelligence: "Lebensintelligenz",
      intelligenceDescription: "Die KI bringt proaktiv Einblicke auf Basis deines Lebensgraphen hervor. Sie bemerkt, was du vielleicht verpasst und stellt die richtigen Fragen.",
      needsAttention: "Benötigt Aufmerksamkeit",
      suggestionDescription: "Deine Mind-Dimension ist niedrig (3.8/10). Du hast 4 Mal diese Woche Arbeitsstress erwähnt. Vielleicht ist es Zeit zu erforschen warum?",
      basedOnSources: "Basierend auf 14 Quellen",
      deepProactiveInsights: "Tiefe und Proaktive Einblicke",
      insightsDescription: "Die KI wartet nicht darauf, dass du fragst. Sie bemerkt proaktiv Spannungen, Muster und Chancen in deinem Lebensgraphen.",
      insightsBasedHistory: "Einblicke basierend auf deiner echten Geschichte",
      proactiveQuestions: "Proaktive Fragen, die dich zum Nachdenken bringen",
      identifyTensions: "Identifiziert Spannungen zwischen Werten und Handlungen",
      yourStory: "Deine Geschichte",
      findAnythingInstantly: "Finde Alles, Sofort",
      storyDescription: "Die leistungsstarke semantische Suche ermöglicht es dir, jeden Moment nach Bedeutung zu finden, nicht nur nach Schlüsselwörtern. Deine gesamte Reise, perfekt organisiert.",
      semanticSearch: "Semantische Suche: finde nach Bedeutung, nicht nur Wörtern",
      organizedCategories: "Organisiert nach Kategorien: Geist, Finanzen, Verbindungen",
      momentConnectedGraph: "Jeder Moment mit deinem Lebensgraphen verbunden",
      moments: "MOMENTE",
      searchPlaceholder: "Durchsuche deine Momente...",
      allCategories: "Alle Kategorien",
      today: "Heute",
      todayMoment: "Erstaunliches Gespräch mit Sarah über gemeinsamen Umzug ins Ausland. Sie ist voll dabei.",
      thisWeek: "Diese Woche",
      weekMoment: "Schwieriges Kundengespräch. Fühlte mich unterbewertet. Fange an zu hinterfragen, ob dieser Job mit meinen Werten übereinstimmt.",
      knowYouPercentage: "Ich kenne dich zu 82%",
      contextAwareSuggestion: "Basierend auf deinen letzten 3 Momenten scheint der Arbeitsstress zuzunehmen. Willst du darüber reden?",
      personalDashboard: "Persönliches Dashboard",
      journeyAtGlance: "Deine Reise auf einen Blick",
      dashboardDescription: "Eine lebendige Momentaufnahme deines multidimensionalen Lebens. Emotionsfluss, Dimensionstrends und deine wöchentliche 'beste Zeit' alles in einer Ansicht.",
      emotionFlowChart: "Emotionsfluss-Diagramm der letzten 14 Tage",
      dimensionsTrends: "5 Dimensionstrends mit Sparklines",
      identifyBestTime: "Identifiziere deine wöchentliche 'beste Zeit'",
      emotionFlow: "Emotionsfluss",
      lastDaysTracked: "Letzte 14 Tage • 6 Tage verfolgt",
      challengesToOvercome: "Zu Überwindende Herausforderungen",
      challengeExample1: "Angst vor Finanzieller Instabilität",
      challengeDescription1: "Wiederkehrende Angst um Geld",
      external: "Extern",
      challengeExample2: "Elterlicher Druck nach Stabilität",
      challengeDescription2: "Familienerwartungen im Konflikt mit Wünschen",
      swipeForChallenges: "← Wische für mehr Herausforderungen",
      identifiedChallenges: "Identifizierte Herausforderungen",
      faceRealObstacles: "Stelle Dich Deinen Echten Hindernissen",
      challengesDescription: "Die KI zeigt nicht nur das Gute. Sie identifiziert echte Herausforderungen, die dein Wachstum blockieren und kategorisiert sie für Klarheit.",
      challengeCategories: "Herausforderungskategorisierung: intern, extern, relational",
      quantifiedIntensity: "Quantifizierte Intensität (1-10)",
      automaticPrioritization: "Automatische Priorisierung von Herausforderungen",
      yourAiGuides: "Deine KI-Guides",
      aiSuggestValues: "KI analysiert deine Momente, um Werte vorzuschlagen, die widerspiegeln, wer du wirklich bist",
      dimensionBody: "Körper",
      dimensionMind: "Geist",
      dimensionHeart: "Herz",
      dimensionSoul: "Seele",
      dimensionMatter: "Materie",
      evolutionDescriptionFull: "Sieh deine Evolution in 5 Dimensionen: Körper, Geist, Herz, Seele, Materie. Radar-Diagramm, Trends im Zeitverlauf und wann du dich am besten fühlst.",
      fiveDimensionsRadarFull: "5 vitale Dimensionen mit Radar-Visualisierung",
      emotionFlowTrendFull: "Emotionsfluss und Trends der letzten 14 Tage",
      rhythmTrackingFull: "Rhythmus-Tracking: Entdecke, wann du dich am besten fühlst",
      goalAlignmentTitle: "Ziel-Ausrichtung",
      goalAlignmentDesc: "Verfolge deinen Fortschritt zu dem, was wirklich zählt. Sieh, wie deine täglichen Handlungen mit deinen langfristigen Zielen übereinstimmen.",
      goalAlignmentSubtitle: "Wie gut du deine Ziele verfolgst",
      weeklyRhythmTitle: "Dein Rhythmus",
      weeklyRhythmDesc: "Entdecke, wann du dich in der Woche am besten fühlst. Identifiziere Muster in deiner Energie und Stimmung.",
      weeklyRhythmSubtitle: "Wann du dich am besten fühlst",
      patternJoyDescription: "Du zeigst starke emotionale Freude in gemeinsamen Momenten mit deinem Partner, besonders durch Gesten der Zuneigung.",
      patternJoySuggestion: "Du könntest diese Momente öfter wertschätzen, um die Verbindung zu stärken.",
      patternWorkDescription: "Wiederkehrende Frustration durch die Bankroutine, die als einschränkend wahrgenommen wird.",
      decisionOptionDescription: "Ein skalierbares KI-Projekt entwickeln, um finanzielle Sicherheit zu gewährleisten",
      goalExample1: "Ein stabiles aber freies Leben schaffen, weg von Mailand, mit der Familie reisen",
      goalExample2: "Eine große, glückliche Familie gründen",
      goalExample3: "Ein authentischeres und verbundeneres Leben mit Sarah aufbauen",
      homeWelcomeDescription: "Dein personalisiertes Zuhause empfängt dich mit täglichen Einblicken, bedeutungsvollen Zitaten und einem vollständigen Bild deiner Reise.",
      aiProactiveDescription: "Die KI wartet nicht darauf, dass du fragst. Sie sendet Einblicke, wenn du sie brauchst, und verbindet Punkte zwischen deinen Momenten.",
      searchContextDescription: "Durchsuche deine Momente wie du Google durchsuchen würdest. Die KI versteht den Kontext, nicht nur Schlüsselwörter.",
      intelligenceQuoteText: "Hey, deine Werte—Freiheit, Verbindung mit Sarah und Familie—sind eine Roadmap. Erinnerst du dich an deinen Wunsch, in London zu leben für Authentizität und den Kampf mit dem Unternehmensalltag? Die Spannung ist real.",
      intelligenceQuoteWarn: "Offenbaren, was du tolerierst und was du nicht tolerieren wirst—wie Toxizität oder Einschränkung.",
      intelligenceQuoteQuestion: "Wie würde es aussehen, deine Werte vollständig zu wählen?",
      actionTodayText: "Setz dich heute Abend mit Emily hin—wirklich hinsetzen, ohne Ablenkungen—und frage, was am meisten auf die aktuelle Situation drückt...",
      actionWeekText: "Bis Ende der Woche skizziere drei konkrete Schritte, die dich deinem Ziel näher bringen, nach Mailand zu ziehen...",
      joyQuoteText: "Freude mit Clara ist keine Flucht, sie ist die Wurzel dessen, was du Freiheit nennst.",
      challengeExample1Full: "Die Monotonie und Unzufriedenheit, die durch die Bankroutine erzeugt wird.",
      challengeDescription1Full: "Du bist zutiefst frustriert von deiner Bankarbeit und empfindest sie als einschränkend und wenig anregend.",
      challengeExample2Full: "Sarahs Jobverlust und plötzliche Kürzung der Bankprovisionen.",
      challengeDescription2Full: "Hat wirtschaftliche Instabilität und Stress im Paar verursacht.",
      challengesAiDescription: "Die KI identifiziert deine echten Lebensherausforderungen: situativ, relational, intern. Jede mit Intensität und Vorschlägen.",
      analyticsLabel: "Analytics Dashboard",
      analyticsTitle: "Lies Deinen Inneren Rhythmus",
      analyticsDescription: "Jede Emotion hat ein Muster. Jedes Hoch und Tief erzählt eine Geschichte. Sieh, wie sich deine Dimensionen im Laufe der Zeit verändern und entdecke die Zyklen, die dein Leben formen.",
      analyticsBullet1: "Erkenne steigende oder fallende Trends, bevor du sie spürst",
      analyticsBullet2: "Verstehe, welche Dimensionen jetzt Aufmerksamkeit brauchen",
      analyticsBullet3: "Sieh deinen Fortschritt als kontinuierliche Wellen, nicht isolierte Punkte"
    },
    privacy: {
      title: "Deine Privatsphäre ist heilig",
      subtitle: "Deine tiefsten Gedanken verdienen den höchsten Schutz. Wir sind ein unabhängiges Unternehmen, das nur unseren Nutzern Rechenschaft ablegt.",
      encryption: {
        title: "Militärische Verschlüsselung",
        desc: "Deine Daten sind mit AES-256-Verschlüsselung geschützt, demselben Standard, den Banken und Regierungen verwenden. End-to-End-Sicherheit im Ruhezustand und während der Übertragung.",
        badge: "AES-256-Verschlüsselung"
      },
      noSelling: {
        title: "Niemals Verkauft",
        desc: "Wir verkaufen deine Daten nicht. Niemals. Punkt. Wir sind kein Werbeunternehmen. Du bist der Kunde, nicht das Produkt.",
        badge: "0% Datenverkauf"
      },
      control: {
        title: "Volle Kontrolle",
        desc: "Exportiere oder lösche deine Daten jederzeit mit einem Klick. Es ist dein Leben, deine Daten, deine Wahl. Keine Fragen gestellt.",
        badge: "Jederzeit Exportieren"
      },
      compliance: "Zertifizierungen & Compliance",
      humanStatement: "Wir sind ein unabhängiges Unternehmen, das nur unseren Nutzern Rechenschaft ablegt. Deine Daten sind vollständig verschlüsselt im Ruhezustand und während der Übertragung. Wir verkaufen niemals deine Daten oder verwenden sie zum Trainieren von KI-Modellen. Deine Privatsphäre ist keine Funktion—sie ist unser Fundament."
    },
    testimonial: {
      quote: "Es ist wie ein weiser Freund, der alles über mich weiß und mir hilft, mich klar zu sehen.",
      author: "Eine bewusstere Version von dir"
    },
    finalCta: {
      title: "Bereit, dich selbst zu treffen?",
      subtitle: "Starte kostenlos. Keine Kreditkarte erforderlich.",
      button: "Jetzt Beginnen"
    },
    footer: {
      tagline: "Bewusstes Leben beginnt hier.",
      privacy: "Datenschutz",
      terms: "AGB"
    },
    auth: {
      back: "Zurück",
      loginTitle: "Willkommen zurück",
      loginSubtitle: "Melde dich an, um fortzufahren",
      registerTitle: "Erstelle dein Konto",
      registerSubtitle: "Beginne deine persönliche Wachstumsreise",
      email: "E-Mail",
      password: "Passwort",
      confirmPassword: "Passwort bestätigen",
      firstName: "Vorname",
      lastName: "Nachname",
      loginButton: "Anmelden",
      registerButton: "Konto erstellen",
      orContinueWith: "oder weiter mit",
      google: "Mit Google fortfahren",
      noAccount: "Noch kein Konto?",
      haveAccount: "Bereits ein Konto?",
      signUp: "Registrieren",
      signIn: "Anmelden",
      passwordMismatch: "Passwörter stimmen nicht überein",
      passwordTooShort: "Passwort muss mindestens 8 Zeichen haben",
      connectionError: "Verbindungsfehler. Bitte erneut versuchen.",
      loginError: "Anmeldefehler",
      registerError: "Registrierungsfehler"
    }
  },
  fr: {
    nav: {
      login: "Connexion",
      getStarted: "Commencer Gratuitement"
    },
    hero: {
      tagline: "Ton Système d'Exploitation pour une Vie Consciente",
      title: "Enfin, comprends-toi.",
      subtitle: "Un compagnon IA qui apprend ton histoire, reconnaît tes schémas et t'aide à prendre des décisions alignées avec qui tu es vraiment.",
      cta: "Commence Ton Voyage",
      ctaSecondary: "Découvre Comment Ça Marche"
    },
    heroRotating: {
      headline1: {
        title: "Toute ton histoire, connectée",
        subtitle: "Vois comment tes moments se connectent. Comprends tes schémas. Prends de meilleures décisions."
      },
      headline2: {
        title: "12 guides IA qui te comprennent vraiment",
        subtitle: "De l'ami honnête au guerrier stoïque. Choisis la voix dont tu as besoin aujourd'hui."
      },
      headline3: {
        title: "Arrête de répéter les mêmes erreurs",
        subtitle: "Ton IA se souvient de tes décisions passées et t'aide à en tirer des leçons."
      },
      headline4: {
        title: "Enfin, comprends-toi. Profondément.",
        subtitle: "Capture des moments. Observe ce qui se répète. Grandis authentiquement."
      },
      trustBadge: "Aucune carte de crédit requise • Commence gratuitement aujourd'hui",
      trustPills: {
        aiPowered: "Propulsé par l'IA",
        private: "100% Privé",
        languages: "5 Langues",
        patterns: "Reconnaissance de Schémas"
      }
    },
    socialProof: {
      trusted: "Utilisé par des penseurs conscients dans le monde entier",
      users: "Utilisateurs Actifs",
      countries: "Pays",
      moments: "Moments Suivis"
    },
    pricing: {
      title: "Commence gratuitement. Mise à niveau quand tu es prêt.",
      subtitle: "Aucune carte de crédit requise. Annule à tout moment.",
      monthly: "Mensuel",
      annual: "Annuel",
      savePercent: "Économise 40%",
      free: {
        name: "Explorer",
        price: "Gratuit Pour Toujours",
        cta: "Commencer Gratuitement",
        features: [
          "50 moments",
          "30 schémas détectés",
          "20 décisions cartographiées",
          "100 messages de chat",
          "Les 12 personnalités IA",
          "Saisie vocale et texte",
          "Analyses de base",
          "5 langues supportées"
        ]
      },
      premium: {
        name: "Oracle",
        priceMonthly: "€9.99/mois",
        priceAnnual: "€99/an",
        priceAnnualFull: "€119.88",
        badge: "Le Plus Populaire",
        cta: "Commencer l'Essai Gratuit de 7 Jours",
        features: [
          "Moments illimités",
          "Schémas illimités",
          "Décisions illimitées",
          "Chat illimité",
          "Analyses avancées",
          "Saisie de photos",
          "Recherche sémantique",
          "Historique d'évolution du profil",
          "Support prioritaire",
          "Accès anticipé aux nouvelles fonctionnalités"
        ]
      },
      noCreditCard: "Sans Carte",
      cancelAnytime: "Annule Quand Tu Veux"
    },
    trust: {
      title: "Tes pensées restent tiennes",
      quote: "Nous sommes une entreprise indépendante. Nous ne répondons qu'à nos utilisateurs. Tes données sont entièrement cryptées au repos et en transit. Nous ne vendons jamais tes données ni ne les utilisons pour entraîner des modèles d'IA.",
      bullets: [
        "Cryptage de niveau militaire (AES-256)",
        "Zéro vente de données. Jamais.",
        "Exporte toutes tes données à tout moment",
        "Conforme RGPD",
        "Tes données ne sont PAS utilisées pour entraîner l'IA"
      ]
    },
    faq: {
      title: "Questions Fréquemment Posées",
      questions: [
        {
          q: "Qu'est-ce qu'Egovex ?",
          a: "Egovex est ton compagnon alimenté par l'IA qui t'aide à te comprendre en connectant les moments de ta vie, en trouvant des schémas et en t'aidant à prendre de meilleures décisions."
        },
        {
          q: "Comment connecte-t-il mes moments ?",
          a: "Chaque fois que tu partages un moment, notre IA recherche des expériences similaires, des schémas et des connexions avec ton passé. C'est comme avoir un ami sage qui se souvient de tout et t'aide à voir la vue d'ensemble."
        },
        {
          q: "Quelles sont les 12 personnalités IA ?",
          a: "Chaque personnalité a sa psychologie distincte, son style de communication et son approche de coaching. Choisis en fonction de ton humeur et de tes besoins - de L'Ami Honnête au Guerrier Stoïque."
        },
        {
          q: "Mes données sont-elles privées ?",
          a: "Oui. Cryptage de niveau militaire. Nous ne vendons jamais de données ni ne les utilisons pour l'entraînement de l'IA. Exporte quand tu veux."
        },
        {
          q: "Qu'est-ce qui est inclus dans le plan gratuit ?",
          a: "50 moments, 30 schémas, 20 décisions, 100 messages de chat, les 12 personnalités, saisie vocale et texte, et analyses de base."
        },
        {
          q: "Puis-je l'utiliser dans ma langue ?",
          a: "Oui. Egovex supporte 5 langues : Anglais, Italien, Espagnol, Allemand et Français."
        },
        {
          q: "En quoi Egovex diffère-t-il des autres applications de journalisation ?",
          a: "Graphe sémantique, 12 personnalités IA, intelligence décisionnelle, détection de schémas et évolution du profil. Ce n'est pas seulement du journaling - c'est un système d'exploitation de vie."
        },
        {
          q: "Puis-je annuler à tout moment ?",
          a: "Oui. Annule à tout moment depuis les paramètres de ton compte. Sans questions."
        }
      ]
    },
    features: {
      title: "Trois piliers de la connaissance de soi",
      subtitle: "Egovex construit un graphe sémantique de ton existence",
      awareness: {
        title: "Conscience de Soi Profonde",
        desc: "Découvre des schémas que tu n'avais jamais remarqués. Comprends ce qui déclenche tes émotions."
      },
      decisions: {
        title: "Intelligence Décisionnelle",
        desc: "Apprends de tes choix passés. Vois des décisions similaires et leurs résultats avant de t'engager."
      },
      growth: {
        title: "Croissance Authentique",
        desc: "Des insights basés sur tes vraies données, pas des conseils génériques. Un progrès aligné avec tes valeurs."
      }
    },
    showcaseMirror: {
      label: "Ton Miroir",
      title: "Qui Tu Es Maintenant",
      desc: "L'IA génère un profil psychologique profond de tous tes moments. Vois ton moi authentique avec clarté.",
      exampleText: "Tu es guidé par un profond désir de liberté, de connexion significative avec tes proches et de construire une vie familiale épanouissante. Tu te déplaces entre le désir de stabilité et le souhait de vivre dans un environnement qui reflète authenticité et connexion.",
      exampleQuestion: "Comment pourrais-tu transformer cet équilibre en réalité concrète ?",
      tag1: "Orienté Croissance",
      tag2: "Axé sur la Famille",
      bullet1: "Synthèse profonde de personnalité de tes vraies données",
      bullet2: "Identifie les tensions clés entre valeurs et actions",
      bullet3: "Évolue au fur et à mesure que tu grandis et changes"
    },
    showcaseValues: {
      label: "Tes Valeurs",
      title: "Ce Qui Compte Pour Toi",
      desc: "Tes valeurs sont le fondement de chaque décision. Étiquette-les et modifie-les au fur et à mesure que tu découvres ce qui compte vraiment.",
      exampleValue1: "Liberté et exploration",
      exampleValue2: "Amour et connexion familiale",
      exampleValue3: "Authenticité personnelle",
      exampleValue4: "Sécurité financière",
      exampleValue5: "Connexion profonde",
      exampleValue6: "Équilibre intérieur",
      bullet1: "Valeurs étiquetées que tu peux toujours modifier",
      bullet2: "L'IA suggère des valeurs basées sur tes moments",
      bullet3: "L'alignement décisionnel montre la correspondance des valeurs"
    },
    showcasePeople: {
      label: "Personnes Importantes",
      title: "Qui Compte Pour Toi",
      desc: "Suis les relations qui façonnent ta vie. Vois à quelle fréquence elles apparaissent dans ton histoire et quels rôles elles jouent.",
      relationship1: "Partenaire",
      relationship2: "Ami et mentor",
      mentioned: "mentionné",
      bullet1: "Détectées automatiquement de tes moments",
      bullet2: "Vois les schémas relationnels au fil du temps",
      bullet3: "Comprends qui influence tes décisions"
    },
    showcaseGoals: {
      label: "Tes Objectifs",
      title: "Où Tu Vas",
      desc: "Définis tes objectifs avec tes propres mots. L'IA t'aide à suivre tes progrès et à rester aligné avec ce que tu veux vraiment.",
      exampleGoal1: "Créer une vie stable mais libre, en voyageant avec la famille",
      exampleGoal2: "Construire une grande famille heureuse",
      exampleGoal3: "Vivre une vie plus authentique et connectée",
      exampleGoal4: "Atteindre un succès professionnel significatif",
      bullet1: "Objectifs dans ton style conversationnel",
      bullet2: "L'IA suit les progrès à travers tes moments",
      bullet3: "Vois l'alignement entre objectifs et actions quotidiennes"
    },
    howItWorks: {
      title: "Comment Egovex transforme ta vie",
      step1: {
        title: "Partage tes moments",
        desc: "Parle à ton compagnon IA de ta journée, tes décisions et tes sentiments. Voix ou texte, tu choisis."
      },
      step2: {
        title: "L'IA construit ton contexte",
        desc: "Chaque moment se connecte à ton histoire de vie. Les schémas émergent. Les insights surgissent."
      },
      step3: {
        title: "Prends de meilleures décisions",
        desc: "Face à une décision, tu as toute ton histoire comme sagesse. Fini les erreurs répétées."
      }
    },
    personalities: {
      title: "12 personnalités IA, chacune unique",
      subtitle: "Choisis la voix dont tu as besoin aujourd'hui. Du soutien doux à l'amour dur. Chaque guide te comprend profondément."
    },
    showcaseLabels: {
      seeInAction: "Voir en Action",
      psychologicalProfile: "Profil Psychologique",
      yourValues: "Tes Valeurs",
      addButton: "Ajouter",
      importantPeople: "Personnes Importantes",
      family: "Famille",
      love: "Amour",
      work: "Travail",
      growth: "Croissance",
      yourGoals: "Tes Objectifs",
      active: "actifs",
      patternDetection: "Détection de Schémas",
      aiFindsPatterns: "L'IA trouve des schémas répétés dans ta vie",
      seeConnectionsMoments: "Vois les connexions entre moments et émotions",
      getPersonalizedSuggestions: "Obtiens des suggestions personnalisées pour grandir",
      patternsIdentified: "Schémas Identifiés",
      joyInConnection: "Joie dans la Connexion de Couple",
      emotional: "Émotionnel",
      observed: "observé",
      contextDescription1: "Après des conversations intimes avec le partenaire",
      momentExample1: "Moments de présence profonde et vulnérabilité ensemble",
      workStress: "Stress du Travail Répétitif",
      situational: "Situationnel",
      contextDescription2: "Environnement de travail non aligné avec les valeurs",
      swipeForMore: "← Glisse pour plus de schémas",
      decisionIntelligence: "Intelligence Décisionnelle",
      resolveDoubtsWithData: "Résoudre les Doutes avec les Données",
      compareOptions: "Comparer les Options",
      comparisonQuestion: "Devrais-je me lancer dans le freelancing ou rester stable dans mon travail actuel ?",
      decisionAnalysis: "Analyse de Décision",
      pros: "Avantages",
      proAlignedSecurity: "Aligné avec le désir de sécurité financière",
      proMobilityFreedom: "Permet mobilité et liberté",
      proHighEarning: "Haut potentiel de gains",
      cons: "Inconvénients",
      conTimeEffort: "Nécessite temps et effort initiaux",
      conShortTermStress: "Stress à court terme",
      conEconomicUncertainty: "Incertitude économique initiale",
      seeFullAnalysis: "Voir l'analyse complète →",
      proConAnalysis: "Analyse POUR/CONTRE basée sur ton histoire",
      alignmentPercentage: "Pourcentage d'alignement avec tes valeurs",
      consciousDecisions: "Décisions conscientes, pas impulsions",
      yourEvolution: "Ton Évolution",
      trackHolisticGrowth: "Suis Ta Croissance Holistique",
      evolutionDescription: "Egovex suit 5 dimensions vitales de ton bien-être dans une visualisation radar dynamique. Vois où tu prospères et où tu as besoin de soin.",
      fiveDimensionsRadar: "5 dimensions vitales avec visualisation radar",
      emotionFlowTrend: "Flux émotionnel et tendances des 14 derniers jours",
      rhythmTracking: "Suivi du rythme : découvre quand tu te sens le mieux",
      yourBestTime: "⭐ Ton Meilleur Moment",
      bestTimeDay: "Vendredi Soir",
      heartSoulPeak: "Cœur et Âme au niveau max",
      lifeIntelligence: "Intelligence de Vie",
      intelligenceDescription: "L'IA fait émerger proactivement des perspectives basées sur ton graphe de vie. Elle remarque ce que tu pourrais manquer et pose les bonnes questions.",
      needsAttention: "Nécessite Attention",
      suggestionDescription: "Ta dimension Esprit est basse (3.8/10). Tu as mentionné le stress au travail 4 fois cette semaine. Peut-être est-il temps d'explorer pourquoi ?",
      basedOnSources: "Basé sur 14 sources",
      deepProactiveInsights: "Perspectives Profondes et Proactives",
      insightsDescription: "L'IA n'attend pas que tu demandes. Elle remarque proactivement tensions, schémas et opportunités dans ton graphe de vie.",
      insightsBasedHistory: "Perspectives basées sur ton histoire réelle",
      proactiveQuestions: "Questions proactives qui te font réfléchir",
      identifyTensions: "Identifie les tensions entre valeurs et actions",
      yourStory: "Ton Histoire",
      findAnythingInstantly: "Trouve Tout, Instantanément",
      storyDescription: "La recherche sémantique puissante te permet de trouver n'importe quel moment par signification, pas seulement mots-clés. Tout ton voyage, parfaitement organisé.",
      semanticSearch: "Recherche sémantique : trouve par signification, pas seulement mots",
      organizedCategories: "Organisé par catégories : Esprit, Finances, Connexions",
      momentConnectedGraph: "Chaque moment connecté à ton graphe de vie",
      moments: "MOMENTS",
      searchPlaceholder: "Cherche dans tes moments...",
      allCategories: "Toutes catégories",
      today: "Aujourd'hui",
      todayMoment: "Conversation incroyable avec Sarah sur notre déménagement à l'étranger ensemble. Elle est pleinement alignée.",
      thisWeek: "Cette Semaine",
      weekMoment: "Appel difficile avec client. Je me suis senti sous-estimé. Je commence à me demander si ce travail s'aligne avec mes valeurs.",
      knowYouPercentage: "Je te connais à 82%",
      contextAwareSuggestion: "Selon tes 3 derniers moments, il semble que le stress au travail augmente. Tu veux en parler ?",
      personalDashboard: "Tableau de Bord Personnel",
      journeyAtGlance: "Ton Voyage en un Coup d'Œil",
      dashboardDescription: "Un instantané vivant de ta vie multidimensionnelle. Flux émotionnel, tendances des dimensions et ton 'meilleur moment' hebdomadaire tout en une vue.",
      emotionFlowChart: "Graphique de flux émotionnel des 14 derniers jours",
      dimensionsTrends: "Tendances des 5 dimensions avec sparklines",
      identifyBestTime: "Identifie ton 'meilleur moment' hebdomadaire",
      emotionFlow: "Flux Émotionnel",
      lastDaysTracked: "14 derniers jours • 6 jours suivis",
      challengesToOvercome: "Défis à Surmonter",
      challengeExample1: "Peur de l'Instabilité Financière",
      challengeDescription1: "Anxiété récurrente à propos de l'argent",
      external: "Externe",
      challengeExample2: "Pression des Parents pour la Stabilité",
      challengeDescription2: "Attentes familiales en conflit avec désirs",
      swipeForChallenges: "← Glisse pour plus de défis",
      identifiedChallenges: "Défis Identifiés",
      faceRealObstacles: "Affronte Tes Vrais Obstacles",
      challengesDescription: "L'IA ne montre pas que le bon. Elle identifie de vrais défis qui bloquent ta croissance et les catégorise pour plus de clarté.",
      challengeCategories: "Catégorisation des défis : internes, externes, relationnels",
      quantifiedIntensity: "Intensité quantifiée (1-10)",
      automaticPrioritization: "Priorisation automatique des défis",
      yourAiGuides: "Tes Guides IA",
      aiSuggestValues: "L'IA analyse tes moments pour suggérer des valeurs qui reflètent qui tu es vraiment",
      dimensionBody: "Corps",
      dimensionMind: "Esprit",
      dimensionHeart: "Cœur",
      dimensionSoul: "Âme",
      dimensionMatter: "Matière",
      evolutionDescriptionFull: "Vois ton évolution sur 5 dimensions : Corps, Esprit, Cœur, Âme, Matière. Graphique radar, tendances dans le temps et quand tu te sens le mieux.",
      fiveDimensionsRadarFull: "5 dimensions vitales avec visualisation radar",
      emotionFlowTrendFull: "Flux émotionnel et tendances des 14 derniers jours",
      rhythmTrackingFull: "Suivi du rythme : découvre quand tu te sens le mieux",
      goalAlignmentTitle: "Alignement des Objectifs",
      goalAlignmentDesc: "Suis ton progrès vers ce qui compte vraiment. Vois comment tes actions quotidiennes s'alignent avec tes objectifs à long terme.",
      goalAlignmentSubtitle: "À quel point tu suis tes objectifs",
      weeklyRhythmTitle: "Ton Rythme",
      weeklyRhythmDesc: "Découvre quand tu te sens le mieux pendant la semaine. Identifie les schémas dans ton énergie et ton humeur.",
      weeklyRhythmSubtitle: "Quand tu te sens le mieux",
      patternJoyDescription: "Tu montres une forte joie émotionnelle dans les moments partagés avec ton partenaire, surtout à travers des gestes d'affection.",
      patternJoySuggestion: "Tu pourrais valoriser ces moments plus souvent pour renforcer la connexion.",
      patternWorkDescription: "Frustration récurrente liée à la routine bancaire, perçue comme limitante.",
      decisionOptionDescription: "Développer un projet d'IA évolutif pour garantir la sécurité financière",
      goalExample1: "Créer une vie stable mais libre, loin de Milan, en voyageant avec la famille",
      goalExample2: "Construire une grande famille heureuse",
      goalExample3: "Construire une vie plus authentique et connectée avec Sarah",
      homeWelcomeDescription: "Ta maison personnalisée t'accueille avec des perspectives quotidiennes, des citations significatives et une vue complète de ton parcours.",
      aiProactiveDescription: "L'IA n'attend pas que tu demandes. Elle envoie des insights quand tu en as besoin, reliant les points entre tes moments.",
      searchContextDescription: "Recherche dans tes moments comme tu rechercherais sur Google. L'IA comprend le contexte, pas seulement les mots-clés.",
      intelligenceQuoteText: "Hé, tes valeurs—liberté, connexion avec Sarah et famille—sont une feuille de route. Tu te souviens de ton désir de vivre à Londres pour l'authenticité et la lutte avec la routine d'entreprise ? La tension est réelle.",
      intelligenceQuoteWarn: "Révèlent ce que tu tolères et ce que tu ne toléreras pas—comme la toxicité ou le confinement.",
      intelligenceQuoteQuestion: "À quoi ressemblerait le fait de choisir pleinement tes valeurs ?",
      actionTodayText: "Assieds-toi avec Emily ce soir—vraiment t'asseoir, sans distractions—et demande ce qui pèse le plus sur la situation actuelle...",
      actionWeekText: "D'ici la fin de la semaine, trace trois étapes concrètes qui te rapprochent de ton objectif de déménager à Milan...",
      joyQuoteText: "La joie avec Clara n'est pas une fuite, c'est la racine de ce que tu appelles liberté.",
      challengeExample1Full: "La monotonie et l'insatisfaction générée par la routine bancaire.",
      challengeDescription1Full: "Tu es profondément frustré par ton travail bancaire, le ressentant comme limitant et peu stimulant.",
      challengeExample2Full: "La perte d'emploi de Sarah et la réduction soudaine des commissions bancaires.",
      challengeDescription2Full: "A causé une instabilité économique et du stress dans le couple.",
      challengesAiDescription: "L'IA identifie tes vrais défis de vie : situationnels, relationnels, internes. Chacun avec intensité et suggestions.",
      analyticsLabel: "Tableau de Bord Analytique",
      analyticsTitle: "Lis Ton Rythme Intérieur",
      analyticsDescription: "Chaque émotion a un motif. Chaque haut et bas raconte une histoire. Vois comment tes dimensions évoluent au fil du temps et découvre les cycles qui façonnent ta vie.",
      analyticsBullet1: "Repère les tendances à la hausse ou à la baisse avant de les ressentir",
      analyticsBullet2: "Comprends quelles dimensions ont besoin d'attention maintenant",
      analyticsBullet3: "Vois ta progression comme des vagues continues, pas des points isolés"
    },
    privacy: {
      title: "Ta vie privée est sacrée",
      subtitle: "Tes pensées les plus profondes méritent la plus haute protection. Nous sommes une entreprise indépendante qui ne répond qu'à nos utilisateurs.",
      encryption: {
        title: "Cryptage de Grade Militaire",
        desc: "Tes données sont protégées par cryptage AES-256, le même standard utilisé par les banques et gouvernements. Sécurité de bout en bout au repos et en transit.",
        badge: "Cryptage AES-256"
      },
      noSelling: {
        title: "Jamais Vendues",
        desc: "Nous ne vendons pas tes données. Jamais. Point. Nous ne sommes pas une entreprise publicitaire. Tu es le client, pas le produit.",
        badge: "0% Vente de Données"
      },
      control: {
        title: "Contrôle Total",
        desc: "Exporte ou supprime tes données à tout moment en un clic. C'est ta vie, tes données, ton choix. Sans questions.",
        badge: "Exporte Quand Tu Veux"
      },
      compliance: "Certifications & Conformité",
      humanStatement: "Nous sommes une entreprise indépendante qui ne répond qu'à nos utilisateurs. Tes données sont entièrement cryptées au repos et en transit. Nous ne vendons jamais tes données ni ne les utilisons pour entraîner des modèles d'IA. Ta vie privée n'est pas une fonctionnalité—c'est notre fondation."
    },
    testimonial: {
      quote: "C'est comme avoir un ami sage qui se souvient de tout sur moi et m'aide à me voir clairement.",
      author: "Une version plus consciente de toi"
    },
    finalCta: {
      title: "Prêt à te rencontrer?",
      subtitle: "Commence gratuitement. Aucune carte de crédit requise.",
      button: "Commencer Maintenant"
    },
    footer: {
      tagline: "La vie consciente commence ici.",
      privacy: "Confidentialité",
      terms: "Conditions"
    },
    auth: {
      back: "Retour",
      loginTitle: "Bon retour",
      loginSubtitle: "Connecte-toi pour continuer",
      registerTitle: "Crée ton compte",
      registerSubtitle: "Commence ton parcours de développement personnel",
      email: "Email",
      password: "Mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      firstName: "Prénom",
      lastName: "Nom",
      loginButton: "Se connecter",
      registerButton: "Créer un compte",
      orContinueWith: "ou continue avec",
      google: "Continuer avec Google",
      noAccount: "Pas de compte ?",
      haveAccount: "Déjà un compte ?",
      signUp: "S'inscrire",
      signIn: "Se connecter",
      passwordMismatch: "Les mots de passe ne correspondent pas",
      passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères",
      connectionError: "Erreur de connexion. Réessaye.",
      loginError: "Erreur de connexion",
      registerError: "Erreur d'inscription"
    }
  }
};

export function detectBrowserLanguage(): LandingLanguage {
  if (typeof navigator === 'undefined') return 'en';
  
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  const supported: LandingLanguage[] = ['en', 'it', 'es', 'de', 'fr'];
  
  return supported.includes(browserLang as LandingLanguage) 
    ? browserLang as LandingLanguage 
    : 'en';
}
